/**
 * Product Builder - Web Components for Price Modifier Overlays
 *
 * These custom elements handle the dynamic behavior of overlay components
 * that replace standard variant selectors while still affecting pricing
 * through variant selection.
 */

/**
 * Coordinated Validation System
 * Ensures validation errors appear in DOM order (top to bottom)
 */
window.builderValidation = window.builderValidation || {
  components: new Set(),
  __validationInProgress: false,
  __toastShown: false,
  __toastListenerInstalled: false,

  register(component) {
    this.components.add(component);
  },

  unregister(component) {
    this.components.delete(component);
  },

  // Validate all components for a given product-form, in DOM order
  validateForForm(productForm) {
    if (!productForm) return true;

    // During coordinated validation we only want ONE toast (top-most invalid field),
    // but we still want ALL fields to show their inline/border error state.
    this.__validationInProgress = true;
    this.__toastShown = false;

    const sectionScope = productForm.closest('[id^="shopify-section-"]') || productForm;

    // Collect components from both the registry AND the DOM
    const componentSet = new Set();

    for (const component of this.components) {
      const componentForm = component.getAssociatedProductForm?.();
      if (componentForm === productForm) componentSet.add(component);
    }

    const selector = [
      'select-input-overlay',
      'charm-selector-overlay',
      'text-input-overlay',
      'grouped-input-overlay',
      'builder-text-input',
      'builder-select',
      'builder-upload',
      'builder-charm-selector',
    ].join(',');

    for (const el of sectionScope.querySelectorAll(selector)) {
      const elForm = el.getAssociatedProductForm?.();
      if (elForm && elForm !== productForm) continue;
      componentSet.add(el);
    }

    const formComponents = Array.from(componentSet);

    // Sort by DOM position (top to bottom)
    formComponents.sort((a, b) => {
      const position = a.compareDocumentPosition(b);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });

    // Validate in order; validate EVERYTHING so all fields show their error UI.
    let allValid = true;

    for (const component of formComponents) {
      // Prefer component's own validation API if available
      if (typeof component?.validateWithFeedback === 'function') {
        const ok = component.validateWithFeedback();
        if (!ok) allValid = false;
        continue;
      }

      // Fallback validators for overlays (in case custom element hasn't upgraded yet)
      if (component?.tagName === 'CHARM-SELECTOR-OVERLAY') {
        const ok = this.__fallbackValidateCharmSelectorOverlay(component);
        if (!ok) allValid = false;
      }
    }

    this.__validationInProgress = false;
    return allValid;
  },

  __fallbackValidateCharmSelectorOverlay(component) {
    try {
      const isOptional = component.dataset.optional === 'true';
      const numberOptionPosition = component.dataset.numberOptionPosition;
      const formId = component.querySelector('input[form]')?.getAttribute('form');
      const form = (formId && document.getElementById(formId)) || component.closest('form');

      if (isOptional && numberOptionPosition && form) {
        const numberRadio = form.querySelector(
          `input[type="radio"][data-option-position="${numberOptionPosition}"]:checked`,
        );
        const labelText = numberRadio?.nextElementSibling?.textContent?.trim();
        if (labelText === '0') return true;
      }

      const productId = component.dataset.productId;
      const sectionId = component.dataset.sectionId;
      const key = productId && sectionId ? `charm-selections-${productId}-${sectionId}` : null;
      const stored = key ? JSON.parse(sessionStorage.getItem(key) || '{}') : {};
      const firstValue = stored['0'] || stored[0];

      if (firstValue && String(firstValue).trim() !== '') return true;

      // Show toast + pill error UI
      const message = component.dataset.errorMessage || 'Please make a selection';
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message, tone: 'error' },
        }),
      );

      // Turn legend red on error
      const legend = component.querySelector('legend.text-subdued');
      if (legend) {
        legend.style.color = 'rgb(var(--error-text, 220 38 38))';
        setTimeout(() => {
          legend.style.color = '';
        }, 3000);
      }

      const firstPill = component.querySelector('[data-charm-pill][data-pill-index="0"]');
      if (firstPill) {
        // Add class-based styling (theme CSS) AND an inline fallback to guarantee visibility
        firstPill.classList.add('charm-selector__pill--error');

        // Force-restart animation + ensure red border even if CSS vars get overridden
        firstPill.style.borderColor = 'rgb(var(--error-text, 220 38 38))';
        firstPill.style.animation = 'none';
        void firstPill.offsetHeight; // restart animation
        firstPill.style.animation = 'charm-pill-shake 0.4s ease-in-out';
      }

      return false;
    } catch {
      // If anything goes wrong, fail closed (block submit) with a generic toast
      document.dispatchEvent(
        new CustomEvent('toast:show', {
          detail: { message: 'Please check your selections', tone: 'error' },
        }),
      );
      return false;
    }
  },
};

/**
 * Text Transform Utility
 * Applies case transformation to input values
 * @param {string} value - The input value to transform
 * @param {string} transform - The transformation type: 'capitalize', 'uppercase', 'lowercase', or 'none'
 * @returns {string} The transformed value
 */
const applyTextTransform = (value, transform) => {
  switch (transform) {
    case 'capitalize':
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    case 'uppercase':
      return value.toUpperCase();
    case 'lowercase':
      return value.toLowerCase();
    default:
      return value;
  }
};

/**
 * Compiles a validation pattern (e.g., "[A-Za-z .,'!-]*") into a regex
 * that tests individual characters. Handles \uXXXX escape sequences from data attributes.
 * @param {string} pattern - The validation pattern
 * @returns {RegExp|null} Single-character test regex, or null if no pattern
 */
const compileCharFilterRegex = (pattern) => {
  if (!pattern) return null;
  const converted = pattern.replace(/\\u([0-9A-Fa-f]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  try {
    return new RegExp(`^${converted.replace(/[*+]$/, '')}$`, 'u');
  } catch {
    return null;
  }
};

/**
 * Filters an input's value in-place to only contain characters matching the regex.
 * Preserves cursor position relative to valid characters.
 * @param {HTMLInputElement} input
 * @param {RegExp} charRegex - Single-character test regex from compileCharFilterRegex
 */
const filterInputByPattern = (input, charRegex) => {
  if (!charRegex || !input.value) return false;
  const cursorPos = input.selectionStart;
  const filtered = [...input.value].filter((ch) => charRegex.test(ch)).join('');
  if (filtered !== input.value) {
    const filteredBeforeCursor = [...input.value.substring(0, cursorPos)].filter((ch) => charRegex.test(ch)).join('');
    input.value = filtered;
    input.setSelectionRange(filteredBeforeCursor.length, filteredBeforeCursor.length);
    return true;
  }
  return false;
};

// Form-level validation hooks (single source of truth)
if (!window.builderValidation.__globalListenersInstalled) {
  window.builderValidation.__globalListenersInstalled = true;

  // Suppress the error toasts raised during a validation run: the invalid fields already show their own
  // inline error state (red label / red border / shake), so the banner at the bottom is not needed.
  // Toasts fired outside a validation run (e.g. cart errors) are untouched.
  if (!window.builderValidation.__toastListenerInstalled) {
    window.builderValidation.__toastListenerInstalled = true;
    document.addEventListener(
      'toast:show',
      (event) => {
        if (!window.builderValidation.__validationInProgress) return;

        event.preventDefault?.();
        event.stopImmediatePropagation();
        window.builderValidation.__toastShown = true;
      },
      true,
    );
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const productForm = form.closest('product-form');
      if (!productForm) return;

      if (!window.builderValidation.validateForForm(productForm)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  document.addEventListener(
    'click',
    (event) => {
      const paymentButton = event.target.closest?.('.shopify-payment-button');
      if (!paymentButton) return;

      const productForm = paymentButton.closest('product-form');
      if (!productForm) return;

      if (!window.builderValidation.validateForForm(productForm)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  // ATC click validation (product-form often uses AJAX and may not trigger a real submit event)
  document.addEventListener(
    'click',
    (event) => {
      const submitButton = event.target.closest?.(
        'product-form button[type="submit"], product-form input[type="submit"]',
      );
      if (!submitButton) return;

      // Ignore accelerated checkout (handled above)
      if (submitButton.closest('.shopify-payment-button')) return;

      const productForm = submitButton.closest('product-form');
      if (!productForm) return;

      if (!window.builderValidation.validateForForm(productForm)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true,
  );

  const revealAwaitingButtons = (root) => {
    for (const el of root.querySelectorAll('buy-buttons[data-awaiting-builder]')) {
      el.removeAttribute('data-awaiting-builder');
    }
  };

  revealAwaitingButtons(document);

  // Variant rerenders replace <buy-buttons> with fresh server HTML that re-adds
  // data-awaiting-builder. Observe the DOM so we reveal them immediately.
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;

        if (node.matches?.('buy-buttons[data-awaiting-builder]')) {
          node.removeAttribute('data-awaiting-builder');
        }

        revealAwaitingButtons(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
}

/**
 * Persist line-item properties across Shopify product re-renders.
 *
 * Shopify updates the product DOM (via <product-rerender>) on variant changes, which replaces
 * inputs and clears what the buyer already typed/selected. We persist all properties[...] fields
 * to sessionStorage before the rerender, then restore them afterward.
 */
window.builderPropertiesPersistence = window.builderPropertiesPersistence || {
  STORAGE_PREFIX: 'builder-properties-',

  getSectionKey(sectionScope) {
    const id = sectionScope?.id || '';
    if (id.startsWith('shopify-section-')) return id.replace('shopify-section-', '');
    return id || null;
  },

  getPreferredFormId(sectionScope) {
    return sectionScope?.querySelector('product-form form[id]')?.id || null;
  },

  collect(sectionScope) {
    if (!sectionScope) return {};

    const values = {};
    const els = sectionScope.querySelectorAll(
      'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]',
    );

    for (const el of els) {
      // Skip file inputs (cannot be restored)
      if (el instanceof HTMLInputElement && el.type === 'file') continue;

      const elFormId = el.getAttribute('form') || el.closest('form')?.id || null;

      const name = el.getAttribute('name');
      if (!name) continue;

      if (el instanceof HTMLInputElement && el.type === 'radio') {
        // store selected value per radio group
        if (el.checked) values[name] = { type: 'radio', value: el.value, formId: elFormId };
        continue;
      }

      if (el instanceof HTMLInputElement && el.type === 'checkbox') {
        values[name] = { type: 'checkbox', checked: el.checked, value: el.value, formId: elFormId };
        continue;
      }

      values[name] = { type: 'value', value: el.value, formId: elFormId };
    }

    return values;
  },

  save({ sectionScope }) {
    if (!sectionScope) return;

    const values = this.collect(sectionScope);

    try {
      const sectionKey = this.getSectionKey(sectionScope);
      if (!sectionKey) return;
      sessionStorage.setItem(this.STORAGE_PREFIX + sectionKey, JSON.stringify(values));
    } catch {
      // ignore storage errors
    }
  },

  saveFocus({ sectionScope }) {
    if (!sectionScope) return;

    const active = document.activeElement;
    if (!active || !sectionScope.contains(active)) return;

    if (!(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)) return;

    const name = active.getAttribute('name');
    if (!name) return;

    const formId = active.getAttribute('form') || active.closest('form')?.id || null;
    const sectionKey = this.getSectionKey(sectionScope);
    if (!sectionKey) return;

    const payload = {
      name,
      formId,
      selectionStart: active.selectionStart,
      selectionEnd: active.selectionEnd,
    };

    try {
      sessionStorage.setItem(this.STORAGE_PREFIX + sectionKey + '-focus', JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
  },

  restoreFocus({ sectionScope }) {
    if (!sectionScope) return;

    const sectionKey = this.getSectionKey(sectionScope);
    if (!sectionKey) return;

    let payload;
    try {
      payload = JSON.parse(sessionStorage.getItem(this.STORAGE_PREFIX + sectionKey + '-focus') || '{}');
    } catch {
      payload = {};
    }

    if (!payload.name) return;

    const candidates = sectionScope.querySelectorAll(`[name="${CSS.escape(payload.name)}"]`);
    if (candidates.length === 0) return;

    const orderedCandidates = [];
    for (const el of candidates) {
      const elFormId = el.getAttribute('form') || el.closest('form')?.id || null;
      if (payload.formId && elFormId === payload.formId) {
        orderedCandidates.unshift(el);
      } else {
        orderedCandidates.push(el);
      }
    }

    for (const el of orderedCandidates) {
      if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) continue;
      if (el.disabled) continue;
      if (el.type === 'hidden') continue;

      el.focus();
      if (typeof el.setSelectionRange === 'function' && payload.selectionStart !== null) {
        el.setSelectionRange(payload.selectionStart, payload.selectionEnd ?? payload.selectionStart);
      }
      break;
    }

    try {
      sessionStorage.removeItem(this.STORAGE_PREFIX + sectionKey + '-focus');
    } catch {
      // ignore
    }
  },

  applyToHtmlFragment({ htmlFragment, values }) {
    if (!htmlFragment || !values) return;

    for (const [name, data] of Object.entries(values)) {
      if (!name || !data) continue;

      const candidates = htmlFragment.querySelectorAll?.(`[name="${CSS.escape(name)}"]`) || [];
      if (candidates.length === 0) continue;

      if (data.type === 'radio') {
        for (const el of candidates) {
          if (!(el instanceof HTMLInputElement) || el.type !== 'radio') continue;
          if (el.value === data.value) {
            el.setAttribute('checked', '');
          } else {
            el.removeAttribute('checked');
          }
        }
        continue;
      }

      if (data.type === 'checkbox') {
        for (const el of candidates) {
          if (!(el instanceof HTMLInputElement) || el.type !== 'checkbox') continue;
          if (data.checked) el.setAttribute('checked', '');
          else el.removeAttribute('checked');
        }
        continue;
      }

      for (const el of candidates) {
        if (el instanceof HTMLTextAreaElement) {
          el.textContent = data.value ?? '';
          continue;
        }

        if (el instanceof HTMLSelectElement) {
          for (const opt of el.options) {
            opt.selected = opt.value === data.value;
          }
          continue;
        }

        if (el instanceof HTMLInputElement) {
          el.setAttribute('value', data.value ?? '');
          continue;
        }
      }
    }
  },

  restore({ sectionScope }) {
    if (!sectionScope) return;

    let stored;
    try {
      const sectionKey = this.getSectionKey(sectionScope);
      if (!sectionKey) return;
      stored = JSON.parse(sessionStorage.getItem(this.STORAGE_PREFIX + sectionKey) || '{}');
    } catch {
      stored = {};
    }

    const entries = Object.entries(stored);
    if (entries.length === 0) return;

    const preferredFormId = this.getPreferredFormId(sectionScope);

    for (const [name, data] of entries) {
      if (!name || !data) continue;

      const candidates = sectionScope.querySelectorAll(`[name="${CSS.escape(name)}"]`);
      if (candidates.length === 0) continue;

      // Prefer restoring into the current form id if we can detect it, otherwise try any candidate.
      const orderedCandidates = [];
      for (const el of candidates) {
        const elFormId = el.getAttribute('form') || el.closest('form')?.id || null;
        if (preferredFormId && elFormId === preferredFormId) {
          orderedCandidates.unshift(el);
        } else {
          orderedCandidates.push(el);
        }
      }

      for (const el of orderedCandidates) {
        const elFormId = el.getAttribute('form') || el.closest('form')?.id || null;
        if (preferredFormId && elFormId && elFormId !== preferredFormId) continue;

        if (data.type === 'radio') {
          if (el instanceof HTMLInputElement && el.type === 'radio') {
            el.checked = el.value === data.value;
            if (el.checked) el.dispatchEvent(new Event('change', { bubbles: true }));
          }
          continue;
        }

        if (data.type === 'checkbox') {
          if (el instanceof HTMLInputElement && el.type === 'checkbox') {
            el.checked = !!data.checked;
            el.dispatchEvent(new Event('change', { bubbles: true }));
          }
          continue;
        }

        if ('value' in el) {
          el.value = data.value ?? '';
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  },
};

if (!window.builderPropertiesPersistence.__installed) {
  window.builderPropertiesPersistence.__installed = true;

  // Save before Shopify swaps DOM (product-rerender listens in bubbling phase)
  document.addEventListener(
    'product:rerender',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.id) return;

      const sectionScope = form.closest('[id^="shopify-section-"]');
      if (!sectionScope || !sectionScope.id) return;

      const sectionId = sectionScope.id;
      const values = window.builderPropertiesPersistence.collect(sectionScope);
      window.builderPropertiesPersistence.saveFocus({ sectionScope });
      window.builderPropertiesPersistence.save({ sectionScope });

      // Inject values into the HTML fragment BEFORE product-rerender swaps the DOM.
      // This is the most reliable way to prevent fields from clearing.
      window.builderPropertiesPersistence.applyToHtmlFragment({
        htmlFragment: event.detail?.htmlFragment,
        values,
      });

      // Restore after product-rerender has replaced DOM (product-rerender does replaceWith in the same tick)
      const restore = () => {
        const newSectionScope = document.getElementById(sectionId);
        if (!newSectionScope) return;
        window.builderPropertiesPersistence.restore({ sectionScope: newSectionScope });
        window.builderPropertiesPersistence.restoreFocus({ sectionScope: newSectionScope });
      };

      requestAnimationFrame(() => requestAnimationFrame(restore));
    },
    true,
  );

  // Restore on initial load too (helps when navigating back / cached sessions)
  window.addEventListener('DOMContentLoaded', () => {
    const sections = document.querySelectorAll('[id^="shopify-section-"]');
    for (const sectionScope of sections) {
      window.builderPropertiesPersistence.restore({ sectionScope });
    }
  });
}

/**
 * Select Input Overlay
 * Handles multiple popover-based select dropdowns based on variant count
 * Example: "Number of Letters" = 3 renders 3 select popovers
 */
if (!customElements.get('select-input-overlay')) {
  // Closure variable for page load ID (shared across all instances, resets on page reload)
  let selectInputOverlayPageLoadId = null;
  const getSelectInputOverlayPageLoadId = () => {
    if (!selectInputOverlayPageLoadId) {
      selectInputOverlayPageLoadId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
    return selectInputOverlayPageLoadId;
  };

  customElements.define(
    'select-input-overlay',
    class extends HTMLElement {
      #boundOnChange;
      #boundOnFormSubmit;
      #boundOnPaymentButtonClick;
      #boundOnDropdownChange;

      connectedCallback() {
        // Radio inputs inside popovers (for option selection)
        this.optionInputs = this.querySelectorAll('[data-select-input]');
        // Buttons that trigger popovers
        this.buttons = this.querySelectorAll('[data-select-button]');
        // Hidden radio inputs for variant selection (Number of X)
        this.variantRadios = this.querySelectorAll('.sr-only input[type="radio"]');
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.sectionId;
        this.errorMessage = this.dataset.errorMessage || 'Please make a selection';
        this.placeholderText = this.dataset.placeholderText;

        // Dropdown elements (for variant dropdown mode)
        this.dropdownButton = this.querySelector('[data-dropdown-button]');
        this.dropdownInputs = this.querySelectorAll('[data-dropdown-input]');
        this.dropdownConfig = this.querySelector('[data-dropdown-config]');
        this.selectFields = this.querySelectorAll('.select-input-overlay__field');

        // Initialize dropdown if present
        if (this.dropdownButton && this.dropdownInputs.length > 0) {
          // Bind dropdown change events BEFORE initialization so the handler
          // saves to sessionStorage when #initializeDropdown dispatches a
          // synthetic change — preventing an infinite re-render loop in
          // quick-buy modals (where the URL is never updated with ?variant).
          this.#boundOnDropdownChange = this.#onDropdownChange.bind(this);
          for (const input of this.dropdownInputs) {
            input.addEventListener('change', this.#boundOnDropdownChange);
          }

          this.#initializeDropdown();
        } else {
          // Restore saved values from sessionStorage (non-dropdown mode)
          this.#restoreSelections();
        }

        // Bind change event to all option inputs
        this.#boundOnChange = this.#onChange.bind(this);
        for (const input of this.optionInputs) {
          input.addEventListener('change', this.#boundOnChange);
        }

        // Register with coordinated validation system
        window.builderValidation?.register(this);

        // Listen for form submission to validate
        this.parentForm = this.#findParentForm();
        if (this.parentForm) {
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.parentForm.addEventListener('submit', this.#boundOnFormSubmit, true);
        }

        // Intercept accelerated checkout button clicks
        this.#setupPaymentButtonValidation();
      }

      disconnectedCallback() {
        window.builderValidation?.unregister(this);

        for (const input of this.optionInputs) {
          input.removeEventListener('change', this.#boundOnChange);
        }

        // Clean up dropdown event listeners
        if (this.#boundOnDropdownChange) {
          for (const input of this.dropdownInputs) {
            input.removeEventListener('change', this.#boundOnDropdownChange);
          }
        }

        if (this.parentForm && this.#boundOnFormSubmit) {
          this.parentForm.removeEventListener('submit', this.#boundOnFormSubmit, true);
        }

        // Clean up payment button listener
        if (this.#boundOnPaymentButtonClick) {
          document.removeEventListener('click', this.#boundOnPaymentButtonClick, true);
        }
      }

      // Public method for form-level validation
      validateWithFeedback() {
        const isValid = this.#validate();
        if (!isValid) this.#showError();
        return isValid;
      }

      // Public method for coordinated validation
      getAssociatedProductForm() {
        return this.parentForm?.closest('product-form') || this.closest('product-form');
      }

      #findParentForm() {
        const formId = this.optionInputs[0]?.getAttribute('form');
        return this.closest('form') || (formId && document.getElementById(formId));
      }

      #isZeroSelectedForNumberOption() {
        // If we don't have a number option position, treat as optional (legacy behavior)
        const numberOptionPosition = this.dataset.numberOptionPosition;
        if (!numberOptionPosition) return true;

        const parentForm = this.parentForm || this.#findParentForm();
        if (!parentForm) return false;

        const numberRadio = parentForm.querySelector(
          `input[type="radio"][data-option-position="${numberOptionPosition}"]:checked`,
        );
        const labelText = numberRadio?.nextElementSibling?.textContent?.trim();
        return labelText === '0';
      }

      #setupPaymentButtonValidation() {
        // Use document-level delegation to handle dynamically replaced payment buttons
        this.#boundOnPaymentButtonClick = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#boundOnPaymentButtonClick, true);
      }

      #onPaymentButtonClick(event) {
        // Check if click is within a shopify payment button
        const paymentButton = event.target.closest('.shopify-payment-button');
        if (!paymentButton) return;

        // Check if this payment button is associated with our form
        const clickedProductForm = paymentButton.closest('product-form');
        const ourProductForm = this.parentForm?.closest('product-form') || this.closest('product-form');

        if (clickedProductForm !== ourProductForm) return;

        // Now validate
        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#showError();
        }
      }

      #getStorageKey() {
        return `select-input-overlay-${this.productId}-${this.sectionId}`;
      }

      #getCurrentVariantId() {
        for (const radio of this.variantRadios) {
          if (radio.checked) {
            return radio.value;
          }
        }
        return null;
      }

      #onChange(event) {
        const input = event.target;
        const selectIndex = parseInt(input.dataset.selectIndex, 10);
        const value = input.value;

        // Update button text
        this.#updateButtonText(selectIndex, value);

        // Save to sessionStorage
        this.#saveSelection(selectIndex, value);

        if (!value) {
          this.#clearSelectionsAfter(selectIndex);
        }

        // Enable/disable subsequent buttons based on this selection
        this.#updateButtonStates();

        // Update variant based on highest filled select
        const highestFilled = this.#getHighestFilledSelectNumber();
        this.#updateNumberVariant(highestFilled);

        // Save variant ID AFTER variant has been updated
        this.#saveVariantId();

        // Clear error state when selection is made
        if (value) {
          this.#clearError();
        }
      }

      #updateButtonText(index, value) {
        const button = this.querySelector(`[data-select-button][data-select-index="${index}"]`);
        const textSpan = button?.querySelector('[data-button-text]');
        if (!textSpan) return;

        if (!value) {
          textSpan.textContent = this.placeholderText;
          return;
        }

        // Get price from button data attribute (if prices are shown)
        const selectPrice = button?.dataset.selectPrice;
        textSpan.textContent = this.#formatDisplayText(value, selectPrice);
      }

      #clearSelectionsAfter(index) {
        for (const input of this.optionInputs) {
          const inputIndex = parseInt(input.dataset.selectIndex, 10);
          if (inputIndex <= index) continue;
          input.checked = false;
        }

        for (const button of this.buttons) {
          const buttonIndex = parseInt(button.dataset.selectIndex, 10);
          if (buttonIndex <= index) continue;
          this.#updateButtonText(buttonIndex, '');
        }
      }

      #formatDisplayText(value, price) {
        if (!value) return value;

        // Truncate if longer than 15 characters
        const maxLength = 15;
        let displayValue = value.length > maxLength ? value.substring(0, maxLength) + '...' : value;

        // Add price if available
        const cleanedPrice = price ? price.replace(/<[^>]*>/g, '').trim() : '';
        if (cleanedPrice) {
          displayValue += ` + ${cleanedPrice}`;
        }

        return displayValue;
      }

      #updateButtonStates() {
        // Skip in dropdown mode - dropdown controls visibility/enablement
        if (this.dropdownButton) return;

        const selections = this.#getStoredSelections();
        let previousFilled = true;

        for (const button of this.buttons) {
          const index = parseInt(button.dataset.selectIndex, 10);
          const field = button.closest('.select-input-overlay__field');

          if (index === 0) {
            button.disabled = false;
            previousFilled = selections[0] !== '';
          } else {
            button.disabled = !previousFilled;
            previousFilled = previousFilled && selections[index] !== '';
          }

          if (field) {
            for (const radio of field.querySelectorAll('[data-select-input]')) {
              radio.disabled = button.disabled;
            }
          }
        }
      }

      #getHighestFilledSelectNumber() {
        const selections = this.#getStoredSelections();
        let highest = 0;

        for (let i = 0; i < selections.length; i++) {
          if (selections[i] !== '') {
            highest = i + 1;
          } else {
            break; // Stop at first empty (sequential filling)
          }
        }

        return highest;
      }

      #updateNumberVariant(targetNumber) {
        const targetValue = targetNumber > 0 ? String(targetNumber) : '1';

        for (const radio of this.variantRadios) {
          const label = radio.nextElementSibling;
          const labelText = label?.textContent?.trim();

          if (labelText === targetValue) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }

      #saveSelection(index, value) {
        const key = this.#getStorageKey();
        const stored = sessionStorage.getItem(key);
        const selections = stored ? JSON.parse(stored) : {};

        if (value) {
          selections[index] = value;
        } else {
          delete selections[index];
          // Clear all subsequent selections when clearing this one
          for (const k of Object.keys(selections)) {
            if (parseInt(k, 10) > index) {
              delete selections[k];
            }
          }
        }

        sessionStorage.setItem(key, JSON.stringify(selections));
      }

      #saveVariantId() {
        const key = this.#getStorageKey();
        const stored = sessionStorage.getItem(key);
        const selections = stored ? JSON.parse(stored) : {};
        selections._variantId = this.#getCurrentVariantId();
        sessionStorage.setItem(key, JSON.stringify(selections));
      }

      #getStoredSelections() {
        const key = this.#getStorageKey();
        const stored = sessionStorage.getItem(key);
        const selections = stored ? JSON.parse(stored) : {};

        // Convert to array format based on number of buttons
        const result = [];
        for (let i = 0; i < this.buttons.length; i++) {
          result[i] = selections[i] || '';
        }

        return result;
      }

      #restoreSelections() {
        const key = this.#getStorageKey();
        const stored = sessionStorage.getItem(key);

        // In dropdown mode, skip variant ID check - the variant changes with the dropdown
        // but we want to preserve selections across those changes (similar to inline modes)
        if (!this.dropdownButton && stored) {
          try {
            const storedData = JSON.parse(stored);
            const storedVariantId = storedData._variantId;
            const currentVariantId = this.#getCurrentVariantId();

            // Clear storage if variant IDs don't match (or no stored variant ID)
            if (!storedVariantId || (currentVariantId && storedVariantId !== currentVariantId)) {
              sessionStorage.removeItem(key);
            }
          } catch {
            sessionStorage.removeItem(key);
          }
        }

        // Now get selections (will be empty if we just cleared)
        const selections = this.#getStoredSelections();

        for (let i = 0; i < selections.length; i++) {
          const savedValue = selections[i];
          if (savedValue) {
            // Update button text
            this.#updateButtonText(i, savedValue);

            // Check the corresponding radio input
            const radio = this.querySelector(
              `[data-select-input][data-select-index="${i}"][value="${CSS.escape(savedValue)}"]`,
            );
            if (radio) {
              radio.checked = true;
            }
          }
        }

        // Update button states after restoration
        this.#updateButtonStates();
      }

      #onFormSubmit(event) {
        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#showError();
        }
      }

      #validate() {
        // Optional if Number option is explicitly set to 0
        if (this.dataset.optional === 'true' && this.#isZeroSelectedForNumberOption()) return true;

        const selections = this.#getStoredSelections();

        // In dropdown mode, all visible selects are required
        if (this.dropdownButton) {
          const checkedDropdown = this.querySelector('[data-dropdown-input]:checked');
          const visibleCount = checkedDropdown ? parseInt(checkedDropdown.dataset.selectCount, 10) : 1;

          for (let i = 0; i < visibleCount; i++) {
            if (selections[i] === '') {
              return false;
            }
          }
          return true;
        }

        // Non-dropdown mode: check if at least the first select has a value
        return selections[0] !== '';
      }

      #showError() {
        const selections = this.#getStoredSelections();

        // Turn top-level legend/label red on error
        const legend = this.querySelector('legend.text-subdued, .select-input-overlay__dropdown-label');
        if (legend) {
          legend.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        // Find the first button that needs a value
        let errorButton = this.buttons[0];
        let errorFieldIndex = 0;
        if (this.dropdownButton) {
          // In dropdown mode, find first visible button without a selection
          const checkedDropdown = this.querySelector('[data-dropdown-input]:checked');
          const visibleCount = checkedDropdown ? parseInt(checkedDropdown.dataset.selectCount, 10) : 1;

          for (let i = 0; i < visibleCount; i++) {
            if (selections[i] === '' && this.buttons[i]) {
              errorButton = this.buttons[i];
              errorFieldIndex = i;
              break;
            }
          }
        }

        // Turn the specific field label red
        const fieldLabels = this.querySelectorAll('.select-input-overlay__label');
        const errorLabel = fieldLabels[errorFieldIndex];
        if (errorLabel) {
          errorLabel.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        if (errorButton) {
          errorButton.classList.add('select-input-overlay__button--error');

          // Auto-remove border error after 3 seconds
          setTimeout(() => {
            errorButton.classList.remove('select-input-overlay__button--error');
            if (legend) legend.style.color = '';
            if (errorLabel) errorLabel.style.color = '';
          }, 3000);
        }

        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: this.errorMessage,
              tone: 'error',
            },
          }),
        );

        // Show inline error message - find error element in the specific field
        const allErrorElements = this.querySelectorAll('[data-error-element]');
        const errorEl = allErrorElements[errorFieldIndex];
        if (errorEl) {
          errorEl.textContent = this.errorMessage;
          errorEl.hidden = false;
        }
      }

      #clearError() {
        // Reset top-level legend/label color
        const legend = this.querySelector('legend.text-subdued, .select-input-overlay__dropdown-label');
        if (legend) legend.style.color = '';

        // Reset all field label colors
        const fieldLabels = this.querySelectorAll('.select-input-overlay__label');
        for (const label of fieldLabels) {
          label.style.color = '';
        }

        // Clear border error from all buttons
        for (const button of this.buttons) {
          button.classList.remove('select-input-overlay__button--error');
        }

        // Clear inline error message from all error elements
        const allErrorElements = this.querySelectorAll('[data-error-element]');
        for (const errorEl of allErrorElements) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }

      // ========== Dropdown Methods (Variant Dropdown Mode) ==========

      #getDropdownStorageKey() {
        return `select-input-dropdown-${this.productId}-${this.sectionId}`;
      }

      #initializeDropdown() {
        let config = { defaultIndex: 0, defaultSelectCount: 1 };
        try {
          config = JSON.parse(this.dropdownConfig?.textContent || '{}');
        } catch {
          /* ignore parsing errors */
        }

        // Check if user has a stored selection (persists across Shopify re-renders within same page load)
        const storageKey = this.#getDropdownStorageKey();
        const storedData = sessionStorage.getItem(storageKey);
        let storedSelectCount = null;

        // Only use stored value if it's from the current page load
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            const currentPageLoadId = getSelectInputOverlayPageLoadId();
            if (parsed.pageLoadId === currentPageLoadId) {
              storedSelectCount = parsed.selectCount;
            }
          } catch {
            // Invalid JSON, ignore
          }
        }

        const visibleInputs = Array.from(this.dropdownInputs);
        const alreadyCheckedInput = visibleInputs.find((input) => input.checked);

        // Priority: stored selection > already checked (URL variant) > config default
        let targetSelectCount;
        if (storedSelectCount) {
          targetSelectCount = parseInt(storedSelectCount, 10);
        } else if (alreadyCheckedInput) {
          targetSelectCount = parseInt(alreadyCheckedInput.dataset.selectCount, 10);
        } else {
          targetSelectCount = config.defaultSelectCount;
        }

        // Find the input matching the target count
        const targetInput =
          visibleInputs.find((input) => {
            const selectCount = parseInt(input.dataset.selectCount, 10);
            return selectCount === targetSelectCount;
          }) || visibleInputs[0];

        const url = new URL(window.location.href);
        const hasVariantInUrl = url.searchParams.has('variant');
        const insideQuickBuy = !!this.closest('quick-buy-modal');

        if (targetInput) {
          if (!targetInput.checked) {
            targetInput.checked = true;
          }
          // Dispatch change event on fresh page load without variant in URL
          // This syncs the URL with the dropdown's default selection.
          // Skip inside quick-buy modals: URL syncing doesn't apply there,
          // and the async re-render it triggers can race with user input.
          if (!storedSelectCount && !hasVariantInUrl && !insideQuickBuy) {
            targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // Update button text and visible select fields
        const checkedInput = this.querySelector('[data-dropdown-input]:checked');
        if (checkedInput) {
          const buttonText = this.querySelector('[data-dropdown-text]');
          if (buttonText) {
            buttonText.innerHTML = checkedInput.dataset.displayLabel;
          }
          this.#updateVisibleSelectFields(parseInt(checkedInput.dataset.selectCount, 10));
        }

        // Restore saved select field values (in dropdown mode, we skip variant ID check)
        this.#restoreSelections();
      }

      #onDropdownChange(event) {
        const input = event.target;
        const targetCount = parseInt(input.dataset.selectCount, 10);

        // Store selection to persist across Shopify re-renders (within same page load only)
        const storageKey = this.#getDropdownStorageKey();
        const storageData = JSON.stringify({
          selectCount: targetCount.toString(),
          pageLoadId: getSelectInputOverlayPageLoadId(),
        });
        sessionStorage.setItem(storageKey, storageData);

        // Update button text
        const buttonText = this.querySelector('[data-dropdown-text]');
        if (buttonText) {
          buttonText.innerHTML = input.dataset.displayLabel;
        }

        // Update visible select fields based on selection
        this.#updateVisibleSelectFields(targetCount);

        // Clear any stored values and UI for select fields that are now hidden
        this.#clearStoredSelectionsAfter(targetCount - 1);
        this.#clearSelectionsAfter(targetCount - 1);
      }

      #updateVisibleSelectFields(count) {
        // Show/hide select fields based on count
        for (let i = 0; i < this.selectFields.length; i++) {
          const field = this.selectFields[i];
          const isVisible = i < count;
          field.hidden = !isVisible;

          // In dropdown mode, enable all visible select buttons
          const button = field.querySelector('[data-select-button]');
          if (button) {
            button.disabled = !isVisible;
          }

          for (const radio of field.querySelectorAll('[data-select-input]')) {
            radio.disabled = !isVisible;
          }
        }
      }

      #clearStoredSelectionsAfter(index) {
        const key = this.#getStorageKey();
        const stored = sessionStorage.getItem(key);
        if (!stored) return;

        try {
          const selections = JSON.parse(stored);
          let changed = false;

          for (const k of Object.keys(selections)) {
            if (k === '_variantId') continue;
            if (parseInt(k, 10) > index) {
              delete selections[k];
              changed = true;
            }
          }

          if (changed) {
            sessionStorage.setItem(key, JSON.stringify(selections));
          }
        } catch {
          // Ignore parsing errors
        }
      }
    },
  );
}

/**
 * Charm Selector Overlay
 * Handles pill-based charm selection with modal (supports multiple pills)
 */
if (!customElements.get('charm-selector-overlay')) {
  customElements.define(
    'charm-selector-overlay',
    class extends HTMLElement {
      #boundOnDocumentClick;
      #boundOnInlineClick;
      #boundOnInlineChange;
      #boundOnInlinePopupChange;
      #boundOnFormSubmit;
      #boundOnPaymentButtonClick;

      connectedCallback() {
        // Picker style toggle (metaobject field): "Popup" (default) / "Inline Grid" / "Inline Popup"
        this.pickerStyle = (this.dataset.pickerStyle || 'Popup').trim();

        // Check if in direct variant mode (no Number option, charm list matches option values)
        // Must be checked BEFORE picker style guards, since Inline modes are only for
        // multi-selection flows — direct variant mode always uses Popup behavior.
        this.isDirectVariantMode = this.hasAttribute('data-direct-variant-mode');

        this.isInlineGridMode = this.pickerStyle === 'Inline Grid';
        this.isInlinePopupMode = this.pickerStyle === 'Inline Popup' && !this.isDirectVariantMode;

        this.pills = this.querySelectorAll('[data-charm-pill]');
        this.radioInputs = this.querySelectorAll('input[type="radio"]');
        this.propertyInputs = this.querySelectorAll('[data-property-input]');
        this.placeholderText = this.dataset.placeholderText;
        this.placeholderTextOptional = this.dataset.placeholderTextOptional || this.placeholderText;
        this.maxChars = parseInt(this.dataset.maxChars, 10) || 20;
        this.activePillIndex = null;
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.sectionId;
        this.errorMessage =
          this.dataset.errorMessage || this.getAttribute('data-error-message') || 'Please make a selection';

        // Get hidden property input for direct variant mode
        this.directPropertyInput = this.querySelector('[data-direct-property-input]');

        // Get the drawer element from first pill
        const modalId = this.pills[0]?.getAttribute('aria-controls');
        this.modal = modalId ? document.getElementById(modalId) : null;
        this.modalId = modalId;

        this._rerenderGuard = () => {
          const block = this.closest('[data-block-type][data-block-allow-rerender]');
          if (block) {
            block.removeAttribute('data-block-allow-rerender');
            queueMicrotask(() => block.setAttribute('data-block-allow-rerender', ''));
          }
        };
        document.addEventListener('product:rerender', this._rerenderGuard, true);

        // Inline Grid mode
        if (this.isInlineGridMode) {
          if (this.isDirectVariantMode) {
            // Direct variant + Inline Grid: single flat grid, variant selection on click
            this.addEventListener('click', (e) => {
              const btn = e.target.closest('[data-inline-charm-option]');
              if (!btn || !this.contains(btn)) return;
              e.preventDefault();
              this.#onDirectInlineCharmSelect(btn);
            });

            this.#restoreDirectInlineSelection();
          } else {
            // Multi-selection Inline Grid: count selector + slot blocks
            this.inlineSlots = this.querySelectorAll('[data-inline-slot]');

            this.#boundOnInlineClick = this.#onInlineClick.bind(this);
            this.addEventListener('click', this.#boundOnInlineClick);

            this.#boundOnInlineChange = this.#onInlineChange.bind(this);
            this.addEventListener('change', this.#boundOnInlineChange);

            this.#syncInlineCountFromRadio();
            this.#restoreInlineSelections();
          }

          // Register with coordinated validation system
          window.builderValidation?.register(this);

          // Listen for form submission to validate
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.#findParentForm()?.addEventListener('submit', this.#boundOnFormSubmit, true);

          // Intercept accelerated checkout button clicks
          this.#setupPaymentButtonValidation();

          return;
        }

        // Inline Popup mode: slot headers with pill buttons that open modal
        if (this.isInlinePopupMode) {
          this.inlinePopupSlots = this.querySelectorAll('[data-inline-popup-slot]');

          // Bind change handling for the count radios (uses the theme's native option-value inputs)
          this.#boundOnInlinePopupChange = this.#onInlinePopupChange.bind(this);
          this.addEventListener('change', this.#boundOnInlinePopupChange);

          // Bind pill clicks to open modal (reuses existing #onPillClick logic)
          for (const pill of this.pills) {
            pill.addEventListener('click', this.#onPillClick.bind(this));
          }

          // Bind charm option clicks using document-level event delegation (for modal)
          this.#boundOnDocumentClick = this.#onDocumentClick.bind(this);
          document.addEventListener('click', this.#boundOnDocumentClick);

          // Restore inline popup state/selections (count + slot values)
          this.#syncInlinePopupCountFromRadio();
          this.#restoreInlinePopupSelections();

          // Register with coordinated validation system
          window.builderValidation?.register(this);

          // Listen for form submission to validate (use capture phase to run before ProductForm handler)
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.#findParentForm()?.addEventListener('submit', this.#boundOnFormSubmit, true);

          // Intercept accelerated checkout button clicks
          this.#setupPaymentButtonValidation();

          return;
        }

        // Popup multi-pill mode: ensure empty property inputs are not submitted
        // (we only enable an input once it has a value)
        if (!this.isDirectVariantMode) {
          for (const input of this.propertyInputs) {
            input.disabled = true;
          }
        }

        // Restore any saved selections from sessionStorage (Popup mode)
        if (this.isDirectVariantMode) this.#restoreDirectVariantSelection();
        else this.#restoreSelections();

        // Bind pill clicks
        for (const pill of this.pills) {
          pill.addEventListener('click', this.#onPillClick.bind(this));
        }

        // Bind charm option clicks using document-level event delegation
        this.#boundOnDocumentClick = this.#onDocumentClick.bind(this);
        document.addEventListener('click', this.#boundOnDocumentClick);

        // Register with coordinated validation system
        window.builderValidation?.register(this);

        // Listen for form submission to validate (use capture phase to run before ProductForm handler)
        this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
        this.#findParentForm()?.addEventListener('submit', this.#boundOnFormSubmit, true);

        // Intercept accelerated checkout button clicks
        this.#setupPaymentButtonValidation();

        // Listen for variant changes to update pill count dynamically (only in multi-pill mode)
        if (!this.isDirectVariantMode) {
          this.#listenForNumberOptionChanges();
        }
      }

      disconnectedCallback() {
        window.builderValidation?.unregister(this);

        if (this._rerenderGuard) {
          document.removeEventListener('product:rerender', this._rerenderGuard, true);
        }

        if (this.#boundOnInlineClick) {
          this.removeEventListener('click', this.#boundOnInlineClick);
        }

        if (this.#boundOnInlineChange) {
          this.removeEventListener('change', this.#boundOnInlineChange);
        }

        if (this.#boundOnInlinePopupChange) {
          this.removeEventListener('change', this.#boundOnInlinePopupChange);
        }

        if (this.#boundOnDocumentClick) {
          document.removeEventListener('click', this.#boundOnDocumentClick);
        }

        // Clean up payment button listener
        if (this.#boundOnPaymentButtonClick) {
          document.removeEventListener('click', this.#boundOnPaymentButtonClick, true);
        }

        if (this.#boundOnFormSubmit) {
          this.#findParentForm()?.removeEventListener('submit', this.#boundOnFormSubmit, true);
        }
      }

      // Public method for form-level validation
      validateWithFeedback() {
        const isValid = this.#validate();
        if (!isValid) this.#showError();
        return isValid;
      }

      // Public method for checking validity without showing errors (used by sticky ATC)
      isValid() {
        return this.#validate();
      }

      // Public method for coordinated validation
      getAssociatedProductForm() {
        const parentForm = this.#findParentForm();
        return parentForm?.closest('product-form') || this.closest('product-form');
      }

      #isZeroSelectedForNumberOption() {
        // If we don't have a number option position, treat as optional (legacy behavior)
        const numberOptionPosition = this.dataset.numberOptionPosition;
        if (!numberOptionPosition) return true;

        const parentForm = this.#findParentForm();
        if (!parentForm) return false;

        const numberRadio = parentForm.querySelector(
          `input[type="radio"][data-option-position="${numberOptionPosition}"]:checked`,
        );
        const labelText = numberRadio?.nextElementSibling?.textContent?.trim();
        return labelText === '0';
      }

      #onDocumentClick(event) {
        // Check if click is inside our modal
        const charmOption = event.target.closest('[data-charm-option]');
        if (!charmOption) return;

        // Verify it's inside our modal by checking if it's a descendant
        const parentDrawer = charmOption.closest('x-drawer');
        if (!parentDrawer || parentDrawer.id !== this.modalId) return;

        // Prevent default behavior
        event.preventDefault();

        this.#onCharmSelect({ currentTarget: charmOption });
      }

      #onPillClick(event) {
        // Check if click was on remove button
        const removeBtn = event.target.closest('[data-charm-remove]');

        if (removeBtn) {
          event.stopPropagation();
          const pillIndex = parseInt(event.currentTarget.dataset.pillIndex, 10);

          if (this.isDirectVariantMode) {
            // In direct variant mode, just clear the single pill (no shift)
            this.#clearDirectVariantSelection();
          } else if (this.isInlinePopupMode) {
            // Inline Popup mode: just clear this slot (no shift, variant controlled by count selector)
            this.#clearInlinePopupSlot(pillIndex);
          } else {
            // Popup multi-pill mode: shift remaining pills up
            this.#shiftPillsAndUpdate(pillIndex);
          }
          return;
        }

        const pill = event.currentTarget;

        // Set this pill as active for next charm selection
        this.activePillIndex = parseInt(pill.dataset.pillIndex, 10);

        // Update price display in the modal based on this pill's price (only in multi-pill mode)
        if (!this.isDirectVariantMode) {
          const pillPrice = pill.dataset.pillPrice;
          this.#updateModalPrices(pillPrice);
        }

        // Highlight currently selected option in modal based on pill's value
        this.#highlightSelectedOptionForPill(pill);
      }

      #highlightSelectedOptionForPill(pill) {
        if (!this.modal) return;

        const charmOptions = this.modal.querySelectorAll('[data-charm-option]');

        // Clear all selected states first
        for (const opt of charmOptions) {
          opt.classList.remove('charm-selector__option--selected');
        }

        // Get current value from pill text (if it has a value)
        if (!pill.classList.contains('charm-selector__pill--has-value')) return;

        const pillText = pill.querySelector('[data-pill-text]')?.textContent?.trim();
        if (!pillText) return;

        // Extract charm name from pill text (remove price suffix like " + $10.00")
        const charmName = pillText.replace(/\s*\+\s*\$[\d,.]+$/, '').replace(/\.{3}$/, '');

        // Find and highlight matching option
        for (const opt of charmOptions) {
          const optValue = opt.dataset.value;
          if (optValue && (optValue === charmName || optValue.startsWith(charmName))) {
            opt.classList.add('charm-selector__option--selected');
            break;
          }
        }
      }

      #clearDirectVariantSelection() {
        const pill = this.pills[0];
        if (!pill) return;

        // Reset pill to placeholder text
        const pillTextSpan = pill.querySelector('[data-pill-text]');
        if (pillTextSpan) {
          pillTextSpan.textContent = this.placeholderText;
        }

        // Remove has-value class
        pill.classList.remove('charm-selector__pill--has-value');

        // Remove the remove button
        const removeBtn = pill.querySelector('[data-charm-remove]');
        removeBtn?.remove();

        // Clear hidden line item property
        if (this.directPropertyInput) {
          this.directPropertyInput.value = '';
        }

        // Clear from sessionStorage
        this.#clearDirectVariantSessionStorage();

        // Select the first variant option (default)
        const firstRadio = this.radioInputs[0];
        if (firstRadio) {
          firstRadio.checked = true;
          firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      #getDirectVariantStorageKey() {
        return `charm-direct-${this.productId}-${this.sectionId}`;
      }

      #saveDirectVariantSelection(value, optionId) {
        const key = this.#getDirectVariantStorageKey();
        sessionStorage.setItem(key, JSON.stringify({ value, optionId }));
      }

      #clearDirectVariantSessionStorage() {
        const key = this.#getDirectVariantStorageKey();
        sessionStorage.removeItem(key);
      }

      #restoreDirectVariantSelection() {
        const pill = this.pills[0];
        if (!pill) return;

        // Get the currently selected variant from Shopify (checked radio input)
        const selectedRadio = Array.from(this.radioInputs).find((r) => r.checked);
        if (!selectedRadio) return;

        // Get the value (charm name) from the label next to the radio
        const label = selectedRadio.nextElementSibling;
        const shopifySelectedValue = label?.textContent?.trim();
        if (!shopifySelectedValue) return;

        // Check sessionStorage
        const key = this.#getDirectVariantStorageKey();
        const stored = sessionStorage.getItem(key);

        if (!stored) {
          // No stored selection - this is a fresh visit, show placeholder
          return;
        }

        try {
          const { value: storedValue } = JSON.parse(stored);

          // Only show the selection if sessionStorage matches Shopify's selected variant
          // This ensures sync when user manually changes URL or navigates
          if (storedValue !== shopifySelectedValue) {
            // Mismatch - clear sessionStorage and show placeholder
            sessionStorage.removeItem(key);
            return;
          }

          // Values match - user intentionally selected this, show it
          const pillTextSpan = pill.querySelector('[data-pill-text]');
          if (pillTextSpan) {
            pillTextSpan.textContent = storedValue;
          }

          // Update pill styling
          pill.classList.add('charm-selector__pill--has-value');
          this.#ensureRemoveButton(pill);

          // Restore hidden line item property
          if (this.directPropertyInput) {
            this.directPropertyInput.value = storedValue;
          }
        } catch {
          // Invalid stored data, clear it
          sessionStorage.removeItem(key);
        }
      }

      #updateModalPrices(price) {
        if (!this.modal) return;

        // Hide $0.00 prices in modal (but pills still show them as indicator)
        const cleanedPrice = price ? price.replace(/<[^>]*>/g, '') : '';
        const hasDigit = /\d/.test(cleanedPrice);
        const hasNonZeroDigit = /[1-9]/.test(cleanedPrice);
        const isZeroPrice = !price || (hasDigit && !hasNonZeroDigit);

        const priceElements = this.modal.querySelectorAll('[data-option-price]');
        for (const priceEl of priceElements) {
          if (price && !isZeroPrice) {
            priceEl.innerHTML = `+ ${price}`;
            priceEl.hidden = false;
          } else {
            priceEl.textContent = '';
            priceEl.hidden = true;
          }
        }
      }

      #formatPillDisplayText(value, pillPrice) {
        const cleanedPrice = pillPrice ? pillPrice.replace(/<[^>]*>/g, '').trim() : '';

        // If price data exists (even $0.00), always show it for consistency
        const hasPrice = cleanedPrice !== '';
        const priceSuffix = hasPrice ? ` + ${cleanedPrice}` : '';

        // Calculate max chars for name (reserve space for price suffix)
        const maxNameChars = hasPrice ? Math.max(this.maxChars - priceSuffix.length, 8) : this.maxChars;

        // Truncate name if needed
        const truncatedName = value.length > maxNameChars ? value.substring(0, maxNameChars).trim() + '...' : value;

        return truncatedName + priceSuffix;
      }

      #onCharmSelect(event) {
        if (this.activePillIndex === null) return;

        const option = event.currentTarget;
        const value = option.dataset.value;
        const optionId = option.dataset.optionId;

        // Update selected state visually
        const charmOptions = this.modal?.querySelectorAll('[data-charm-option]') || [];
        for (const opt of charmOptions) {
          opt.classList.toggle('charm-selector__option--selected', opt === option);
        }

        // Get the active pill
        const activePill = this.querySelector(`[data-charm-pill][data-pill-index="${this.activePillIndex}"]`);
        if (!activePill) return;

        // Handle direct variant mode differently
        if (this.isDirectVariantMode) {
          // Update pill text (no price in direct mode since it changes the variant)
          let pillTextSpan = activePill.querySelector('[data-pill-text]');
          if (!pillTextSpan) {
            activePill.innerHTML = '';
            pillTextSpan = document.createElement('span');
            pillTextSpan.className = 'charm-selector__pill-text';
            pillTextSpan.dataset.pillText = '';
            activePill.appendChild(pillTextSpan);
          }
          pillTextSpan.textContent = value;

          // Update pill styling
          activePill.classList.add('charm-selector__pill--has-value');
          this.#ensureRemoveButton(activePill);

          // Update hidden line item property (with _ prefix, hidden from customer)
          if (this.directPropertyInput) {
            this.directPropertyInput.value = value;
          }

          // Save selection to sessionStorage for persistence across re-renders
          this.#saveDirectVariantSelection(value, optionId);

          // Close the modal
          if (this.modal) {
            this.modal.hide?.();
          }

          // Directly update the variant using the option ID
          if (optionId) {
            this.#updateDirectVariant(optionId, value);
          }

          // Reset active pill index
          this.activePillIndex = null;
          return;
        }

        // Multi-pill mode (original behavior)
        const activePropertyInput = this.querySelector(
          `[data-property-input][data-pill-index="${this.activePillIndex}"]`,
        );

        // Get pill price and format display text with price
        const pillPrice = activePill.dataset.pillPrice;
        const displayText = this.#formatPillDisplayText(value, pillPrice);

        // Ensure we have a text span, create one if missing
        let pillTextSpan = activePill.querySelector('[data-pill-text]');
        if (!pillTextSpan) {
          activePill.innerHTML = '';
          pillTextSpan = document.createElement('span');
          pillTextSpan.className = 'charm-selector__pill-text';
          pillTextSpan.dataset.pillText = '';
          activePill.appendChild(pillTextSpan);
        }
        pillTextSpan.textContent = displayText;

        // Update pill styling
        activePill.classList.add('charm-selector__pill--has-value');
        this.#ensureRemoveButton(activePill);

        // Update line item property with the charm name
        if (activePropertyInput) {
          activePropertyInput.value = value;
          activePropertyInput.disabled = false;
        }

        const imageInput = this.querySelector(`[data-image-input][data-pill-index="${this.activePillIndex}"]`);
        if (imageInput) {
          imageInput.value = option.dataset.image || '';
          imageInput.disabled = false;
        }

        // Save selection to sessionStorage for persistence across re-renders
        this.#saveSelection(this.activePillIndex, value);

        // Close the modal
        if (this.modal) {
          this.modal.hide?.();
        }

        // Inline Popup mode: variant is controlled by count selector, not by pill selection
        if (this.isInlinePopupMode) {
          // Add selected styling to pill (has-value enables remove button positioning)
          activePill.classList.add('charm-selector__pill--selected', 'charm-selector__pill--has-value');
          // Save variant ID for restore safeguards
          this.#saveVariantId();
          // Dispatch change event to trigger sticky ATC validity re-check
          this.dispatchEvent(new Event('change', { bubbles: true }));
          // Reset active pill index
          this.activePillIndex = null;
          return;
        }

        // Popup mode: enable next pill and update variant
        this.#enableNextPill(this.activePillIndex);

        // Update the number variant based on which pill was filled (pill index + 1)
        const pillNumber = this.activePillIndex + 1;
        this.#updateNumberVariant(pillNumber);

        // Save variant ID AFTER variant has been updated
        this.#saveVariantId();

        // Reset active pill index
        this.activePillIndex = null;
      }

      #updateDirectVariant(optionId, value) {
        // Re-query radio inputs in case DOM has changed
        const radioInputs = this.querySelectorAll('input[type="radio"][data-option-position]');

        // Find and check the radio input with matching option ID
        for (const radio of radioInputs) {
          if (radio.value === optionId) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }

        // Fallback: try to find by label text matching the value
        for (const radio of radioInputs) {
          const label = radio.nextElementSibling;
          const labelText = label?.textContent?.trim();
          if (labelText === value) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            return;
          }
        }
      }

      #onDirectInlineCharmSelect(btn) {
        const value = btn.dataset.value;
        const optionId = btn.dataset.optionId;
        if (!value) return;

        // Deselect all inline options, then highlight selected
        for (const opt of this.querySelectorAll('[data-inline-charm-option]')) {
          opt.classList.remove('charm-inline__option--selected');
        }
        btn.classList.add('charm-inline__option--selected');

        // Update hidden line item property
        if (this.directPropertyInput) {
          this.directPropertyInput.value = value;
        }

        // Save to sessionStorage
        this.#saveDirectVariantSelection(value, optionId);

        // Update variant via radio input
        if (optionId) {
          this.#updateDirectVariant(optionId, value);
        }

        // Dispatch change so sticky ATC re-checks validity
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }

      #restoreDirectInlineSelection() {
        const key = this.#getDirectVariantStorageKey();
        const stored = sessionStorage.getItem(key);

        const selectedRadio = Array.from(this.querySelectorAll('input[type="radio"]')).find((r) => r.checked);
        if (!selectedRadio) return;

        const label = selectedRadio.nextElementSibling;
        const shopifySelectedValue = label?.textContent?.trim();
        if (!shopifySelectedValue) return;

        if (!stored) return;

        try {
          const { value: storedValue } = JSON.parse(stored);
          if (storedValue !== shopifySelectedValue) {
            sessionStorage.removeItem(key);
            return;
          }

          // Highlight matching inline option
          for (const opt of this.querySelectorAll('[data-inline-charm-option]')) {
            if (opt.dataset.value === storedValue) {
              opt.classList.add('charm-inline__option--selected');
              break;
            }
          }

          // Restore property input value
          if (this.directPropertyInput) {
            this.directPropertyInput.value = storedValue;
          }
        } catch {
          sessionStorage.removeItem(key);
        }
      }

      #enableNextPill(currentIndex) {
        const nextIndex = parseInt(currentIndex, 10) + 1;
        const nextPill = this.querySelector(`[data-charm-pill][data-pill-index="${nextIndex}"]`);

        if (nextPill) {
          nextPill.disabled = false;
        }
      }

      #disableSubsequentPills(fromIndex) {
        const startIndex = parseInt(fromIndex, 10) + 1;

        for (const pill of this.pills) {
          const pillIndex = parseInt(pill.dataset.pillIndex, 10);

          if (pillIndex >= startIndex) {
            pill.disabled = true;
            // Also clear any value this pill had (without triggering another disable cascade)
            this.#clearPillSelection(pillIndex, false);
          }
        }
      }

      #ensureRemoveButton(pill) {
        if (pill.querySelector('[data-charm-remove]')) return;

        const removeBtn = document.createElement('span');
        removeBtn.className = 'charm-selector__remove';
        removeBtn.dataset.charmRemove = '';
        removeBtn.setAttribute('aria-label', 'Clear selection');
        removeBtn.innerHTML = `
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="7.5" cy="7.5" r="7" transform="matrix(-1 0 0 1 15 0)" fill="white" stroke="black"/>
            <path d="M9.75 9.75L5.25 5.25M9.75 5.25L5.25 9.75" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        `;
        pill.appendChild(removeBtn);
      }

      #clearPillSelection(pillIndex, disableSubsequent = true) {
        const pill = this.querySelector(`[data-charm-pill][data-pill-index="${pillIndex}"]`);
        const propertyInput = this.querySelector(`[data-property-input][data-pill-index="${pillIndex}"]`);
        const removeBtn = pill?.querySelector('[data-charm-remove]');

        if (!pill) return;

        // Reset pill text (use appropriate placeholder based on pill index)
        const isFirstPill = pillIndex === 0 || pillIndex === '0';
        const placeholderText = isFirstPill ? this.placeholderText : this.placeholderTextOptional;

        // Ensure we have a text span, create one if missing
        let pillTextSpan = pill.querySelector('[data-pill-text]');
        if (!pillTextSpan) {
          pillTextSpan = document.createElement('span');
          pillTextSpan.className = 'charm-selector__pill-text';
          pillTextSpan.dataset.pillText = '';
          pill.innerHTML = '';
          pill.appendChild(pillTextSpan);
        }
        pillTextSpan.textContent = placeholderText;

        // Remove has-value class
        pill.classList.remove('charm-selector__pill--has-value');

        // Remove the remove button
        if (removeBtn) {
          removeBtn.remove();
        }

        // Clear property input
        if (propertyInput) {
          propertyInput.value = '';
          propertyInput.disabled = true;
        }
        const imageInput = this.querySelector(`[data-image-input][data-pill-index="${pillIndex}"]`);
        if (imageInput) {
          imageInput.value = '';
          imageInput.disabled = true;
        }

        // Remove from sessionStorage
        this.#removeStoredSelection(pillIndex);

        // Disable all subsequent pills and clear their values
        if (disableSubsequent) {
          this.#disableSubsequentPills(pillIndex);
        }
      }

      #shiftPillsAndUpdate(removedPillIndex) {
        // Always use sessionStorage as source of truth (DOM might be stale after re-renders)
        const key = this.#getStorageKey();
        const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

        // Convert stored object to array
        const pillValues = [];
        for (const [idx, value] of Object.entries(stored)) {
          pillValues[parseInt(idx, 10)] = value;
        }

        // Remove the value at the removed index and shift others up
        pillValues.splice(removedPillIndex, 1);

        // Clear all pills first
        for (const pill of this.pills) {
          const idx = parseInt(pill.dataset.pillIndex, 10);
          this.#clearPillSelection(idx, false); // Don't disable subsequent
        }

        // Re-apply shifted values
        for (let i = 0; i < pillValues.length; i++) {
          const value = pillValues[i];
          if (value && value.trim() !== '') {
            this.#setPillValue(i, value);
          }
        }

        // Update sessionStorage with new values and calculate highest filled
        this.#clearStoredSelections();
        let highestFilled = 0;
        for (let i = 0; i < pillValues.length; i++) {
          const value = pillValues[i];
          if (value && value.trim() !== '') {
            this.#saveSelection(i, value);
            // Track highest filled pill (pill number = index + 1)
            if (i + 1 > highestFilled) {
              highestFilled = i + 1;
            }
          }
        }

        // Enable/disable pills based on filled state
        this.#updatePillStates();

        // Update variant based on highest filled pill
        if (highestFilled > 0) {
          this.#updateNumberVariant(highestFilled);
        } else {
          // If no pills filled, select variant "1" as default
          this.#updateNumberVariant(1);
        }

        // Save variant ID AFTER variant has been updated
        this.#saveVariantId();
      }

      #setPillValue(pillIndex, value) {
        const pill = this.querySelector(`[data-charm-pill][data-pill-index="${pillIndex}"]`);
        const propertyInput = this.querySelector(`[data-property-input][data-pill-index="${pillIndex}"]`);

        if (!pill) return;

        // Get pill price and format display text with price
        const pillPrice = pill.dataset.pillPrice;
        const displayText = this.#formatPillDisplayText(value, pillPrice);

        // Ensure we have a text span, create one if missing
        let pillTextSpan = pill.querySelector('[data-pill-text]');
        if (!pillTextSpan) {
          // Remove any existing content and create proper structure
          const removeBtn = pill.querySelector('[data-charm-remove]');
          pill.innerHTML = '';
          pillTextSpan = document.createElement('span');
          pillTextSpan.className = 'charm-selector__pill-text';
          pillTextSpan.dataset.pillText = '';
          pill.appendChild(pillTextSpan);
          // Re-add remove button if it existed
          if (removeBtn) {
            pill.appendChild(removeBtn);
          }
        }
        pillTextSpan.textContent = displayText;

        pill.classList.add('charm-selector__pill--has-value');
        pill.disabled = false;
        this.#ensureRemoveButton(pill);

        if (propertyInput) {
          propertyInput.value = value;
          propertyInput.disabled = false;
        }

        const imageInput = this.querySelector(`[data-image-input][data-pill-index="${pillIndex}"]`);
        if (imageInput) {
          const escapedValue = CSS.escape(value);
          const charmBtn =
            this.modal?.querySelector(`[data-charm-option][data-value="${escapedValue}"]`) ||
            this.querySelector(`[data-inline-charm-option][data-value="${escapedValue}"]`);
          imageInput.value = charmBtn?.dataset?.image || '';
          imageInput.disabled = false;
        }
      }

      #updatePillStates() {
        const propertyInputs = this.querySelectorAll('[data-property-input]');
        let lastFilledIndex = -1;

        // Find the last filled pill
        for (const input of propertyInputs) {
          if (input.value && input.value.trim() !== '') {
            const idx = parseInt(input.dataset.pillIndex, 10);
            if (idx > lastFilledIndex) {
              lastFilledIndex = idx;
            }
          }
        }

        // Enable pills up to and including the one after the last filled
        for (const pill of this.pills) {
          const idx = parseInt(pill.dataset.pillIndex, 10);
          if (idx <= lastFilledIndex + 1) {
            pill.disabled = false;
          } else {
            pill.disabled = true;
          }
        }
      }

      #listenForNumberOptionChanges() {
        const numberOptionPosition = this.dataset.numberOptionPosition;
        if (!numberOptionPosition) return;

        // Listen for variant changes that might affect pill count
        document.addEventListener('variant:changed', () => {
          // The section will re-render with the correct pill count
          // This is handled by Shopify's section rendering
        });
      }

      // Public method to clear all selections
      clearAllSelections() {
        for (const pill of this.pills) {
          const pillIndex = parseInt(pill.dataset.pillIndex, 10);
          this.#clearPillSelection(pillIndex);
        }
        this.#clearStoredSelections();
      }

      #getStorageKey() {
        return `charm-selections-${this.productId}-${this.sectionId}`;
      }

      #getCurrentVariantId() {
        // Re-query radio inputs in case DOM has changed
        const radioInputs = this.querySelectorAll('input[type="radio"][data-option-position]');
        for (const radio of radioInputs) {
          if (radio.checked) {
            return radio.value;
          }
        }
        return null;
      }

      #saveSelection(pillIndex, value) {
        const key = this.#getStorageKey();
        const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
        stored[pillIndex] = value;
        sessionStorage.setItem(key, JSON.stringify(stored));
      }

      #saveVariantId() {
        const key = this.#getStorageKey();
        try {
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
          stored._variantId = this.#getCurrentVariantId();
          sessionStorage.setItem(key, JSON.stringify(stored));
        } catch {
          // Ignore errors
        }
      }

      #removeStoredSelection(pillIndex) {
        const key = this.#getStorageKey();
        const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
        delete stored[pillIndex];
        sessionStorage.setItem(key, JSON.stringify(stored));
      }

      #clearStoredSelections() {
        sessionStorage.removeItem(this.#getStorageKey());
      }

      #restoreSelections() {
        const key = this.#getStorageKey();

        // First check if stored variant ID matches current variant
        const storedRaw = sessionStorage.getItem(key);
        if (storedRaw) {
          try {
            const storedData = JSON.parse(storedRaw);
            const storedVariantId = storedData._variantId;
            const currentVariantId = this.#getCurrentVariantId();

            // Clear storage if variant IDs don't match (or no stored variant ID)
            if (!storedVariantId || (currentVariantId && storedVariantId !== currentVariantId)) {
              sessionStorage.removeItem(key);
              return; // Don't restore anything
            }
          } catch {
            sessionStorage.removeItem(key);
            return;
          }
        }

        // Now restore selections
        const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

        let highestFilled = 0;

        for (const [pillIndex, value] of Object.entries(stored)) {
          // Skip _variantId key
          if (pillIndex === '_variantId') continue;

          const index = parseInt(pillIndex, 10);
          const pill = this.querySelector(`[data-charm-pill][data-pill-index="${index}"]`);
          const propertyInput = this.querySelector(`[data-property-input][data-pill-index="${index}"]`);

          if (!pill || !value || value.trim() === '') continue;

          // Track highest filled pill
          const pillNumber = index + 1;
          if (pillNumber > highestFilled) {
            highestFilled = pillNumber;
          }

          // Get pill price and format display text with price
          const pillPrice = pill.dataset.pillPrice;
          const displayText = this.#formatPillDisplayText(value, pillPrice);

          // Ensure we have a text span, create one if missing
          let pillTextSpan = pill.querySelector('[data-pill-text]');
          if (!pillTextSpan) {
            pill.innerHTML = '';
            pillTextSpan = document.createElement('span');
            pillTextSpan.className = 'charm-selector__pill-text';
            pillTextSpan.dataset.pillText = '';
            pill.appendChild(pillTextSpan);
          }
          pillTextSpan.textContent = displayText;

          // Update pill styling
          pill.classList.add('charm-selector__pill--has-value');
          pill.disabled = false;
          this.#ensureRemoveButton(pill);

          // Restore property input value
          if (propertyInput) {
            propertyInput.value = value;
            propertyInput.disabled = false;
          }

          // Enable the next pill
          this.#enableNextPill(index);
        }

        // Update variant to match restored selections (only if we have selections)
        // Skip on initial restore to avoid re-render loop - variant should already be correct
        // The variant update will be triggered when user interacts
      }

      #updateNumberVariant(pillNumber) {
        if (!pillNumber || pillNumber < 1) return;

        // Find the radio input that matches the pill number
        const numberValue = String(pillNumber);

        // Re-query radio inputs in case DOM has changed
        const radioInputs = this.querySelectorAll('input[type="radio"][data-option-position]');

        for (const radio of radioInputs) {
          const label = radio.nextElementSibling;
          const labelText = label?.textContent?.trim();

          if (labelText === numberValue) {
            // Check the radio and dispatch change event
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }

      #getHighestFilledPillNumber() {
        let highest = 0;

        // Re-query property inputs in case DOM has changed
        const propertyInputs = this.querySelectorAll('[data-property-input]');

        for (const input of propertyInputs) {
          if (input.value && input.value.trim() !== '') {
            const pillIndex = parseInt(input.dataset.pillIndex, 10);
            const pillNumber = pillIndex + 1;
            if (pillNumber > highest) {
              highest = pillNumber;
            }
          }
        }

        return highest;
      }

      #findParentForm() {
        return (
          this.closest('form') ||
          document.querySelector(`form#${this.querySelector('input[form]')?.getAttribute('form')}`)
        );
      }

      #onFormSubmit(event) {
        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#showError();
        }
      }

      #validate() {
        // Optional if Number option is explicitly set to 0
        if (this.dataset.optional === 'true' && this.#isZeroSelectedForNumberOption()) return true;

        // In direct variant mode, check sessionStorage for a selection
        if (this.isDirectVariantMode) {
          const key = this.#getDirectVariantStorageKey();
          const stored = sessionStorage.getItem(key);
          if (!stored) return false;

          try {
            const { value } = JSON.parse(stored);
            return value && value.trim() !== '';
          } catch {
            return false;
          }
        }

        // Inline Grid mode: require slots 1..selectedCount to have values
        if (this.isInlineGridMode) {
          const selectedCount = this.#getSelectedInlineCount();
          if (!selectedCount || selectedCount <= 0) return false;

          const key = this.#getStorageKey();
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

          for (let i = 0; i < selectedCount; i += 1) {
            const v = stored[String(i)] || stored[i];
            if (!v || String(v).trim() === '') return false;
          }

          return true;
        }

        // Inline Popup mode: require slots 1..selectedCount to have values
        if (this.isInlinePopupMode) {
          const selectedCount = this.#getSelectedInlinePopupCount();
          if (!selectedCount || selectedCount <= 0) return false;

          const key = this.#getStorageKey();
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

          for (let i = 0; i < selectedCount; i += 1) {
            const v = stored[String(i)] || stored[i];
            if (!v || String(v).trim() === '') return false;
          }

          return true;
        }

        // Popup multi-pill mode: Check if the first (required) charm pill has a value
        // Use sessionStorage as source of truth (persists across re-renders)
        {
          const key = this.#getStorageKey();
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
          const firstCharmValue = stored['0'] || stored[0];
          return firstCharmValue && firstCharmValue.trim() !== '';
        }
      }

      #showError() {
        // Turn legend red on error (uniform across all modes)
        const legend = this.querySelector('legend.text-subdued');
        if (legend) {
          legend.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        if (this.isInlineGridMode) {
          if (this.isDirectVariantMode) {
            // Direct variant inline grid: shake the single grid
            const grid = this.querySelector('.charm-inline__grid');
            if (grid) {
              grid.classList.add('charm-inline__grid--error');
              setTimeout(() => {
                grid.classList.remove('charm-inline__grid--error');
                if (legend) legend.style.color = '';
              }, 3000);
            }
          } else {
            const selectedCount = this.#getSelectedInlineCount();
            const key = this.#getStorageKey();
            const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

            let firstMissingIndex0 = 0;
            for (let i = 0; i < Math.max(selectedCount, 1); i += 1) {
              const v = stored[String(i)] || stored[i];
              if (!v || String(v).trim() === '') {
                firstMissingIndex0 = i;
                break;
              }
            }

            const slotEl = this.querySelector(`[data-inline-slot][data-slot-index="${firstMissingIndex0 + 1}"]`);
            if (slotEl) {
              slotEl.classList.add('charm-inline__slot--error');
              setTimeout(() => {
                slotEl.classList.remove('charm-inline__slot--error');
                if (legend) legend.style.color = '';
              }, 3000);
            }
          }

          document.dispatchEvent(
            new CustomEvent('toast:show', {
              detail: {
                message: this.errorMessage,
                tone: 'error',
              },
            }),
          );
          return;
        }

        if (this.isInlinePopupMode) {
          const selectedCount = this.#getSelectedInlinePopupCount();
          const key = this.#getStorageKey();
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

          // Find first missing slot and highlight it
          let firstMissingIndex0 = 0;
          for (let i = 0; i < Math.max(selectedCount, 1); i += 1) {
            const v = stored[String(i)] || stored[i];
            if (!v || String(v).trim() === '') {
              firstMissingIndex0 = i;
              break;
            }
          }

          const slotEl = this.querySelector(`[data-inline-popup-slot][data-slot-index="${firstMissingIndex0 + 1}"]`);
          if (slotEl) {
            slotEl.classList.add('charm-inline-popup__slot--error');
            setTimeout(() => {
              slotEl.classList.remove('charm-inline-popup__slot--error');
              if (legend) legend.style.color = '';
            }, 3000);
          }

          document.dispatchEvent(
            new CustomEvent('toast:show', {
              detail: {
                message: this.errorMessage,
                tone: 'error',
              },
            }),
          );
          return;
        }

        const firstPill = this.querySelector('[data-charm-pill][data-pill-index="0"]');
        if (!firstPill) return;

        // Add error class to first pill
        firstPill.classList.add('charm-selector__pill--error');

        // Inline fallback so the error is always visible even if theme variables/styles differ
        firstPill.style.borderColor = 'rgb(var(--error-text, 220 38 38))';
        firstPill.style.animation = 'none';
        void firstPill.offsetHeight; // restart animation
        firstPill.style.animation = 'charm-pill-shake 0.4s ease-in-out';

        // Show error toast
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: this.errorMessage,
              tone: 'error',
            },
          }),
        );

        // Remove error class and inline styles after 3 seconds
        setTimeout(() => {
          firstPill.classList.remove('charm-selector__pill--error');
          firstPill.style.borderColor = '';
          firstPill.style.animation = '';
          if (legend) legend.style.color = '';
        }, 3000);
      }

      #setupPaymentButtonValidation() {
        // Use document-level delegation to handle dynamically replaced payment buttons
        this.#boundOnPaymentButtonClick = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#boundOnPaymentButtonClick, true);
      }

      #onPaymentButtonClick(event) {
        // Check if click is within a shopify payment button
        const paymentButton = event.target.closest('.shopify-payment-button');
        if (!paymentButton) return;

        // Check if this payment button is associated with our form
        const clickedProductForm = paymentButton.closest('product-form');
        const ourProductForm = this.#findParentForm()?.closest('product-form') || this.closest('product-form');

        if (clickedProductForm !== ourProductForm) return;

        // Now validate
        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#showError();
        }
      }

      // -----------------------
      // Inline mode helpers
      // -----------------------

      #onInlineClick(event) {
        const charmBtn = event.target.closest?.('[data-inline-charm-option]');
        if (charmBtn && this.contains(charmBtn)) {
          event.preventDefault();
          this.#onInlineCharmSelect(charmBtn);
        }
      }

      #onInlineChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.type !== 'radio') return;
        if (input.dataset.optionPosition !== this.dataset.optionPosition) return;

        // Update slots based on the newly selected count
        this.#updateInlineSlotsVisibility(this.#getSelectedInlineCount());

        // Persist current variant id for restore safeguards
        this.#saveVariantId();
      }

      #getSelectedInlineCount() {
        const selectedRadio = Array.from(this.radioInputs || []).find((r) => r.checked);
        const label = selectedRadio?.nextElementSibling;
        const text = label?.textContent?.trim() || '';
        const num = parseInt(text, 10);
        return Number.isFinite(num) ? num : 0;
      }

      #syncInlineCountFromRadio() {
        this.#updateInlineSlotsVisibility(this.#getSelectedInlineCount());
      }

      #updateInlineSlotsVisibility(selectedCount) {
        const maxSlots = parseInt(this.dataset.inlineMaxSlots || '', 10);
        const effectiveMax = Number.isFinite(maxSlots) && maxSlots > 0 ? maxSlots : this.inlineSlots?.length || 0;

        const count = Math.max(0, Math.min(selectedCount || 0, effectiveMax));

        // Hide/show slots and clear out-of-range selections
        for (const slotEl of this.inlineSlots || []) {
          const slotIndex1 = parseInt(slotEl.dataset.slotIndex, 10);
          const slotIndex0 = slotIndex1 - 1;
          const shouldShow = slotIndex1 >= 1 && slotIndex1 <= count;
          slotEl.hidden = !shouldShow;

          // Disable/enable the associated hidden property input so only in-range slots submit.
          const input = this.querySelector(`[data-property-input][data-pill-index="${slotIndex0}"]`);
          if (input) input.disabled = !shouldShow;

          if (!shouldShow) {
            this.#clearInlineSlot(slotIndex0);
          }
        }
      }

      #clearInlineSlot(slotIndex0) {
        // Clear hidden property input
        const input = this.querySelector(`[data-property-input][data-pill-index="${slotIndex0}"]`);
        if (input) input.value = '';

        const imageInput = this.querySelector(`[data-image-input][data-pill-index="${slotIndex0}"]`);
        if (imageInput) imageInput.value = '';

        // Remove selected state on buttons within that slot
        for (const btn of this.querySelectorAll(`[data-inline-charm-option][data-slot-index="${slotIndex0}"]`)) {
          btn.classList.remove('charm-inline__option--selected');
        }

        this.#removeStoredSelection(slotIndex0);
      }

      #onInlineCharmSelect(button) {
        const slotIndex0 = parseInt(button.dataset.slotIndex, 10);
        const value = button.dataset.value || '';
        if (!Number.isFinite(slotIndex0)) return;

        // Mark selected button in this slot
        for (const btn of this.querySelectorAll(`[data-inline-charm-option][data-slot-index="${slotIndex0}"]`)) {
          btn.classList.toggle('charm-inline__option--selected', btn === button);
        }

        // Update hidden input
        const input = this.querySelector(`[data-property-input][data-pill-index="${slotIndex0}"]`);
        if (input) input.value = value;

        const imageInput = this.querySelector(`[data-image-input][data-pill-index="${slotIndex0}"]`);
        if (imageInput) imageInput.value = button.dataset.image || '';

        // Persist
        this.#saveSelection(slotIndex0, value);
        this.#saveVariantId();
      }

      #restoreInlineSelections() {
        const key = this.#getStorageKey();

        // NOTE: For Inline Grid mode, we intentionally do NOT check variant ID mismatch.
        // Similar to Inline Popup, the variant changes with the count selector, but we want
        // to preserve slot values across count changes.

        const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

        // Restore values into slots
        for (const [pillIndex, v] of Object.entries(stored)) {
          if (pillIndex === '_variantId') continue;
          const idx = parseInt(pillIndex, 10);
          if (!Number.isFinite(idx)) continue;

          const value = String(v || '').trim();
          if (!value) continue;

          const input = this.querySelector(`[data-property-input][data-pill-index="${idx}"]`);
          if (input) input.value = value;

          const btn = Array.from(this.querySelectorAll(`[data-inline-charm-option][data-slot-index="${idx}"]`)).find(
            (b) => (b.dataset.value || '').trim() === value,
          );
          if (btn) {
            btn.classList.add('charm-inline__option--selected');
            const imageInput = this.querySelector(`[data-image-input][data-pill-index="${idx}"]`);
            if (imageInput) imageInput.value = btn.dataset.image || '';
          }
        }
      }

      // ========== Inline Popup Mode Methods ==========

      #onInlinePopupChange(event) {
        const input = event.target;
        if (!(input instanceof HTMLInputElement)) return;
        if (input.type !== 'radio') return;
        if (input.dataset.optionPosition !== this.dataset.optionPosition) return;

        // Update slots based on the newly selected count
        // Clear hidden slots only if this is a user-initiated change (not a programmatic re-render)
        const clearHiddenSlots = event.isTrusted === true;
        this.#updateInlinePopupSlotsVisibility(this.#getSelectedInlinePopupCount(), clearHiddenSlots);

        // Persist current variant id for restore safeguards
        this.#saveVariantId();
      }

      #getSelectedInlinePopupCount() {
        const selectedRadio = Array.from(this.radioInputs || []).find((r) => r.checked);
        const label = selectedRadio?.nextElementSibling;
        const text = label?.textContent?.trim() || '';
        const num = parseInt(text, 10);
        return Number.isFinite(num) ? num : 0;
      }

      #syncInlinePopupCountFromRadio() {
        // Called during restore/init - don't clear hidden slots (race condition protection)
        this.#updateInlinePopupSlotsVisibility(this.#getSelectedInlinePopupCount(), false);
      }

      #updateInlinePopupSlotsVisibility(selectedCount, clearHiddenSlots = false) {
        const maxSlots = parseInt(this.dataset.inlineMaxSlots || '', 10);
        const effectiveMax = Number.isFinite(maxSlots) && maxSlots > 0 ? maxSlots : this.inlinePopupSlots?.length || 0;

        const count = Math.max(0, Math.min(selectedCount || 0, effectiveMax));

        // Get stored selections to restore visible slots
        const key = this.#getStorageKey();
        const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

        // If user explicitly changed count, clear storage for slots that are now hidden
        if (clearHiddenSlots) {
          let storageModified = false;
          for (let i = count; i < effectiveMax; i++) {
            if (stored[String(i)] !== undefined || stored[i] !== undefined) {
              delete stored[String(i)];
              delete stored[i];
              storageModified = true;
            }
          }
          if (storageModified) {
            sessionStorage.setItem(key, JSON.stringify(stored));
          }
        }

        // Hide/show slots
        for (const slotEl of this.inlinePopupSlots || []) {
          const slotIndex1 = parseInt(slotEl.dataset.slotIndex, 10);
          const slotIndex0 = slotIndex1 - 1;
          const shouldShow = slotIndex1 >= 1 && slotIndex1 <= count;
          slotEl.hidden = !shouldShow;

          // Disable/enable the associated hidden property input so only in-range slots submit.
          const input = this.querySelector(`[data-property-input][data-pill-index="${slotIndex0}"]`);
          if (input) input.disabled = !shouldShow;

          const pill = this.querySelector(`[data-inline-popup-pill][data-pill-index="${slotIndex0}"]`);

          if (shouldShow) {
            // Restore stored value for visible slots (in case they were hidden and now visible again)
            const storedValue = stored[String(slotIndex0)] || stored[slotIndex0];
            if (storedValue && storedValue.trim() !== '') {
              if (input) input.value = storedValue;
              if (pill) {
                const pillText = pill.querySelector('[data-pill-text]');
                if (pillText) pillText.textContent = this.#formatPillDisplayText(storedValue, null);
                pill.classList.add('charm-selector__pill--selected', 'charm-selector__pill--has-value');
                this.#ensureRemoveButton(pill);
              }
            }
          } else {
            // Clear the VISUAL state of hidden slots (pill text/styling)
            if (pill) {
              const pillText = pill.querySelector('[data-pill-text]');
              if (pillText) {
                pillText.textContent = slotIndex0 === 0 ? this.placeholderText : this.placeholderTextOptional;
              }
              pill.classList.remove('charm-selector__pill--selected', 'charm-selector__pill--has-value');
              const removeBtn = pill.querySelector('[data-charm-remove]');
              removeBtn?.remove();
            }
            if (input) input.value = '';
          }
        }

        // Dispatch change event to trigger sticky ATC validity re-check when count changes
        if (clearHiddenSlots) {
          this.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      #clearInlinePopupSlot(slotIndex0) {
        // Clear hidden property input
        const input = this.querySelector(`[data-property-input][data-pill-index="${slotIndex0}"]`);
        if (input) input.value = '';

        // Reset pill text to placeholder and remove styling
        const pill = this.querySelector(`[data-inline-popup-pill][data-pill-index="${slotIndex0}"]`);
        if (pill) {
          const pillText = pill.querySelector('[data-pill-text]');
          if (pillText) {
            pillText.textContent = slotIndex0 === 0 ? this.placeholderText : this.placeholderTextOptional;
          }
          pill.classList.remove('charm-selector__pill--selected', 'charm-selector__pill--has-value');

          // Remove the remove button
          const removeBtn = pill.querySelector('[data-charm-remove]');
          removeBtn?.remove();
        }

        this.#removeStoredSelection(slotIndex0);

        // Dispatch change event to trigger sticky ATC validity re-check
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }

      #restoreInlinePopupSelections() {
        const key = this.#getStorageKey();

        // NOTE: For Inline Popup mode, we intentionally do NOT check variant ID mismatch.
        // The variant is determined by the count selector, and we want to preserve slot values
        // across count changes. The validation logic handles which slots are required based on count.
        // This prevents race conditions where variant briefly changes during re-renders.

        const stored = JSON.parse(sessionStorage.getItem(key) || '{}');

        // Restore values into slots (update pills and hidden inputs)
        for (const [pillIndex, v] of Object.entries(stored)) {
          if (pillIndex === '_variantId') continue;
          const idx = parseInt(pillIndex, 10);
          if (!Number.isFinite(idx)) continue;

          const value = String(v || '').trim();
          if (!value) continue;

          // Update hidden input
          const input = this.querySelector(`[data-property-input][data-pill-index="${idx}"]`);
          if (input) input.value = value;

          // Update pill display
          const pill = this.querySelector(`[data-inline-popup-pill][data-pill-index="${idx}"]`);
          if (pill) {
            const pillText = pill.querySelector('[data-pill-text]');
            if (pillText) {
              // Use formatPillDisplayText with null price for simple truncation
              pillText.textContent = this.#formatPillDisplayText(value, null);
            }
            pill.classList.add('charm-selector__pill--selected', 'charm-selector__pill--has-value');
            this.#ensureRemoveButton(pill);
          }
        }
      }
    },
  );
}

/**
 * Text Input Overlay
 * Handles two modes:
 * 1. Character Count - Single input where character count determines variant
 * 2. Field Count - Multiple inputs where filled field count determines variant
 */
if (!customElements.get('text-input-overlay')) {
  // Closure variable for page load ID (shared across all instances, resets on page reload)
  let textInputOverlayPageLoadId = null;
  const getTextInputOverlayPageLoadId = () => {
    if (!textInputOverlayPageLoadId) {
      textInputOverlayPageLoadId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
    return textInputOverlayPageLoadId;
  };

  customElements.define(
    'text-input-overlay',
    class extends HTMLElement {
      #boundOnInput;
      #boundOnInvalid;
      #boundOnFormSubmit;
      #boundOnPaymentButtonClick;
      #boundOnBlur;
      #boundOnDropdownChange;
      // Add-to-cart fix (2026-10-27): deferred variant re-render so a tap on Add to cart
      // straight from the name box is not swallowed by the re-render.
      #blurSyncTimer = null;
      #submitPendingUntil = 0;
      #charTestRegex;
      #hintTimeout;

      connectedCallback() {
        this.inputs = this.querySelectorAll('[data-text-input]');
        this.radioInputs = this.querySelectorAll('input[type="radio"]');
        this.maxLength = parseInt(this.dataset.maxLength, 10) || 0;
        this.minLength = parseInt(this.dataset.minLength, 10) || 0;
        this.inputCount = parseInt(this.dataset.inputCount, 10) || 1;
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.sectionId;
        this.errorMessage = this.dataset.errorMessage || 'This field is required';
        this.patternMessage = this.dataset.patternMessage || 'Please enter valid characters only';
        this.validationMode = this.dataset.validationMode || 'any';
        this.pattern = this.dataset.pattern;
        this.#charTestRegex = compileCharFilterRegex(this.pattern);
        this.lastFilledCount = 0;

        // Determine mode from variant_trigger
        this.variantTrigger = this.dataset.variantTrigger || 'character-count';
        this.isCharacterMode = this.variantTrigger === 'character-count';

        // Text transformation setting
        this.textTransform = this.dataset.textTransform || 'none';

        // Parse variant prices JSON for live price updates (all modes)
        this.variantPrices = null;
        {
          const pricesScript = this.querySelector('script[data-variant-prices]');
          if (pricesScript) {
            try {
              this.variantPrices = JSON.parse(pricesScript.textContent);
            } catch {
              // Ignore parsing errors
            }
          }
        }

        // Track last char count for character mode
        this.lastCharCount = 0;

        // Ensure clean state on mount
        for (const input of this.inputs) {
          input.classList.remove('text-input-overlay__input--error');
        }

        // Restore saved values from sessionStorage
        this.#restoreValues();
        if (!this.isCharacterMode) {
          this.lastFilledCount = this.#getFilledInputCount();
        } else {
          // Initialize lastCharCount from restored input in character mode
          this.lastCharCount = this.inputs[0]?.value?.length || 0;
        }

        // Bind event listeners to all inputs
        this.#boundOnInput = this.#onInput.bind(this);
        this.#boundOnInvalid = this.#onInvalid.bind(this);
        this.#boundOnBlur = this.#onBlur.bind(this);
        for (const input of this.inputs) {
          input.addEventListener('input', this.#boundOnInput);
          input.addEventListener('invalid', this.#boundOnInvalid);
          // Blur listener for both character mode AND legacy field-count mode
          if (this.isCharacterMode || !this.dropdownButton) {
            input.addEventListener('blur', this.#boundOnBlur);
          }
        }

        // Listen for form submission to validate
        this.parentForm = this.#findParentForm();
        if (this.parentForm) {
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.parentForm.addEventListener('submit', this.#boundOnFormSubmit, true);
        }

        // Intercept accelerated checkout button clicks
        this.#setupPaymentButtonValidation();

        // Dropdown support for Field Count mode
        this.dropdownButton = this.querySelector('[data-dropdown-button]');
        this.dropdownInputs = this.querySelectorAll('[data-dropdown-input]');
        this.dropdownConfig = this.querySelector('[data-dropdown-config]');

        if (this.dropdownButton && this.dropdownInputs.length > 0) {
          this.#boundOnDropdownChange = this.#onDropdownChange.bind(this);
          // Bind directly to each dropdown input for reliable event handling
          for (const dropdownInput of this.dropdownInputs) {
            dropdownInput.addEventListener('change', this.#boundOnDropdownChange);
          }

          // Set initial selection from config
          this.#initializeDropdown();
        }
      }

      disconnectedCallback() {
        clearTimeout(this.#blurSyncTimer);
        window.builderValidation?.unregister(this);

        for (const input of this.inputs) {
          input.removeEventListener('input', this.#boundOnInput);
          input.removeEventListener('invalid', this.#boundOnInvalid);
          if (this.#boundOnBlur) {
            input.removeEventListener('blur', this.#boundOnBlur);
          }
        }

        if (this.parentForm && this.#boundOnFormSubmit) {
          this.parentForm.removeEventListener('submit', this.#boundOnFormSubmit, true);
        }

        // Clean up payment button listener
        if (this.#boundOnPaymentButtonClick) {
          document.removeEventListener('click', this.#boundOnPaymentButtonClick, true);
        }

        // Clean up dropdown listeners
        if (this.#boundOnDropdownChange && this.dropdownInputs) {
          for (const dropdownInput of this.dropdownInputs) {
            dropdownInput.removeEventListener('change', this.#boundOnDropdownChange);
          }
        }

        clearTimeout(this.#hintTimeout);
      }

      // Public method for form-level validation
      validateWithFeedback() {
        return this.#validate();
      }

      // Public method for coordinated validation
      getAssociatedProductForm() {
        return this.parentForm?.closest('product-form') || this.closest('product-form');
      }

      #onInvalid(event) {
        // Prevent native browser validation popup
        event.preventDefault();
        event.stopImmediatePropagation();

        const input = event.target;
        const validity = input.validity;

        // Determine the appropriate error message
        let message;
        if (validity.valueMissing) {
          message = this.errorMessage;
        } else if (validity.patternMismatch) {
          message = this.patternMessage;
        } else if (validity.tooShort) {
          message = `Please enter at least ${this.minLength} characters`;
        } else if (validity.tooLong) {
          message = `Please enter no more than ${this.maxLength} characters`;
        } else {
          message = 'Please check your input';
        }

        // Show our custom validation error
        this.#showValidationError(input, message);
      }

      #setupPaymentButtonValidation() {
        // Use document-level delegation to handle dynamically replaced payment buttons
        this.#boundOnPaymentButtonClick = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#boundOnPaymentButtonClick, true);
      }

      #onPaymentButtonClick(event) {
        // Check if click is within a shopify payment button
        const paymentButton = event.target.closest('.shopify-payment-button');
        if (!paymentButton) return;

        // Check if this payment button is associated with our form
        const clickedProductForm = paymentButton.closest('product-form');
        const ourProductForm = this.parentForm?.closest('product-form') || this.closest('product-form');

        if (clickedProductForm !== ourProductForm) return;

        // Ensure variant is updated before validation in character mode
        // Use silent mode to avoid DOM re-render which would break the payment flow
        if (this.isCharacterMode) {
          const charCount = this.inputs[0]?.value?.length || 0;
          this.#updateNumberVariant(charCount, { silent: true });
        }

        // Now validate - #validate() shows error internally
        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        // In Field Count mode, disable empty inputs so they don't submit empty properties
        if (!this.isCharacterMode) {
          for (const input of this.inputs) {
            if (!input.value.trim()) {
              input.disabled = true;
            }
          }
        }
      }

      #findParentForm() {
        const formId = this.inputs[0]?.getAttribute('form');
        return this.closest('form') || (formId && document.getElementById(formId));
      }

      #isZeroSelectedForNumberOption() {
        // If we don't have a number option position, treat as optional (legacy behavior)
        const numberOptionPosition = this.dataset.numberOptionPosition;
        if (!numberOptionPosition) return true;

        const parentForm = this.parentForm || this.#findParentForm();
        if (!parentForm) return false;

        const numberRadio = parentForm.querySelector(
          `input[type="radio"][data-option-position="${numberOptionPosition}"]:checked`,
        );
        const labelText = numberRadio?.nextElementSibling?.textContent?.trim();
        return labelText === '0';
      }

      #getStorageKey() {
        return `text-input-overlay-${this.productId}-${this.sectionId}`;
      }

      #getCurrentVariantId() {
        for (const radio of this.radioInputs) {
          if (radio.checked) {
            return radio.value;
          }
        }
        return null;
      }

      // Sets the form's hidden variant id to the variant for this letter count (same finish/size).
      // Returns true when it could do so, false when the variant could not be worked out.
      #syncFormVariantId(charCount) {
        if (!this.parentForm || !this.variantPrices) return false;
        const variant = this.#findMatchingVariant(charCount > 0 ? charCount : 1);
        if (!variant?.id) return false;
        const formId = this.parentForm.getAttribute('id');
        const idInputs = new Set(this.parentForm.querySelectorAll('input[name="id"]'));
        if (formId) {
          for (const input of document.querySelectorAll(`input[name="id"][form="${formId}"]`)) idInputs.add(input);
        }
        if (idInputs.size === 0) return false;
        for (const input of idInputs) input.value = String(variant.id);
        return true;
      }

      // Runs the normal variant change (page re-render) after a short delay, and after any
      // add to cart that is in progress, so it never replaces the button while it is pressed.
      #scheduleVariantRerender(charCount, delay) {
        clearTimeout(this.#blurSyncTimer);
        this.#blurSyncTimer = setTimeout(() => {
          if (!this.isConnected) return;
          const waitFor = this.#submitPendingUntil - Date.now();
          if (waitFor > 0) {
            this.#scheduleVariantRerender(charCount, waitFor);
            return;
          }
          // Skip if the shopper is typing in the name box again
          if (this.contains(document.activeElement) && document.activeElement?.hasAttribute?.('data-text-input')) return;
          this.#updateNumberVariant(charCount);
          this.#saveVariantId();
        }, delay);
      }

      #onInput(event) {
        const input = event.target;
        const inputIndex = parseInt(input.dataset.inputIndex, 10);

        // Apply text transformation if configured
        if (this.textTransform !== 'none') {
          const cursorPos = input.selectionStart;
          const originalValue = input.value;
          const transformedValue = applyTextTransform(originalValue, this.textTransform);

          if (transformedValue !== originalValue) {
            input.value = transformedValue;
            input.setSelectionRange(cursorPos, cursorPos);
          }
        }

        // Enforce max length in JS (Android IME can bypass the HTML maxlength attribute)
        if (this.maxLength > 0 && input.value.length > this.maxLength) {
          input.value = input.value.substring(0, this.maxLength);
        }

        // Strip invalid characters in real-time based on validation pattern
        const charsWereFiltered = filterInputByPattern(input, this.#charTestRegex);

        const value = input.value;
        const charCount = value.length;

        // Update character counter for this input
        const formControl = input.closest('.form-control');
        const charCountEl = formControl?.querySelector('[data-char-count]');
        if (charCountEl) {
          charCountEl.textContent = charCount;
        }

        // Remove error state when input has value
        if (value) {
          input.classList.remove('text-input-overlay__input--error');
          const inputField = input.closest('.text-input-overlay__field');
          const errorEl =
            inputField?.querySelector('[data-error-element]') || this.querySelector('[data-error-element]');
          if (errorEl) {
            errorEl.textContent = '';
            errorEl.hidden = true;
          }
        }

        // Show brief informational hint when invalid characters were stripped
        if (charsWereFiltered) {
          this.#showPatternHint(input);
        }

        // Save to sessionStorage
        this.#saveValue(inputIndex, value);

        if (this.isCharacterMode) {
          // Character Count mode: variant based on character count
          this.lastCharCount = charCount;

          // Live price update while typing (no rerender)
          if (this.variantPrices) {
            const targetNumber = charCount > 0 ? charCount : 1;
            const matchingVariant = this.#findMatchingVariant(targetNumber);
            if (matchingVariant) {
              this.#updatePriceDisplay(matchingVariant);
            }
          }

          // Set radio silently (no change event) - full sync happens on blur
          this.#updateNumberVariant(charCount, { silent: true });
          // Keep the form's variant id in step too, so Add to cart always sends the right
          // letter count even if the page has not re-rendered yet
          this.#syncFormVariantId(charCount);
          return;
        } else if (this.dropdownButton) {
          // Dropdown/Field Count mode: no sequential logic, dropdown controls variant
          // Just update filled count for tracking purposes
          this.lastFilledCount = this.#getFilledInputCount();
        } else {
          // Legacy Field Count mode (no dropdown): enable/disable subsequent inputs and update variant
          if (!value) {
            this.#clearInputsAfter(inputIndex);
          }
          this.#updateInputStates();
          this.#enforceSequentialInputs();
          const filledCount = this.#getFilledInputCount();
          if (filledCount !== this.lastFilledCount) {
            this.#updateNumberVariant(filledCount, { silent: true });
            // Live price update without re-render
            if (this.variantPrices) {
              const targetNumber = filledCount > 0 ? filledCount : 1;
              const matchingVariant = this.#findMatchingVariant(targetNumber);
              if (matchingVariant) {
                this.#updatePriceDisplay(matchingVariant);
              }
            }
          }
          this.lastFilledCount = filledCount;
        }

        // Save variant ID AFTER variant has been updated
        this.#saveVariantId();
      }

      #onBlur(event) {
        // Guard against ghost blur events from disconnected/replaced elements (prevents re-render loop)
        if (!this.isConnected) return;

        // Skip variant dispatch when focus moves to another input within this component
        // (e.g., tabbing between fields). The re-render would destroy the DOM and lose focus.
        if (event?.relatedTarget?.hasAttribute?.('data-text-input') && this.contains(event.relatedTarget)) {
          return;
        }

        if (this.isCharacterMode) {
          // Character mode: update variant on blur to avoid cursor jumping while typing.
          // The form's variant id is set right away (no re-render). The re-render that updates
          // the rest of the page is delayed slightly: when the blur was caused by tapping
          // Add to cart, re-rendering immediately replaced the button mid-tap and the add
          // never fired. If an add is in progress, the re-render waits until it has finished.
          const charCount = this.inputs[0]?.value?.length || 0;
          if (this.#syncFormVariantId(charCount)) {
            this.#scheduleVariantRerender(charCount, 350);
          } else {
            this.#updateNumberVariant(charCount);
            this.#saveVariantId();
          }
        } else if (!this.dropdownButton) {
          // Legacy field count: dispatch the actual variant change on blur
          const filledCount = this.#getFilledInputCount();
          this.#updateNumberVariant(filledCount);
          this.#saveVariantId();
        }
      }

      #updateInputStates() {
        // Enable inputs sequentially based on previous input having value
        for (let i = 1; i < this.inputs.length; i++) {
          const prevInput = this.inputs[i - 1];
          const currentInput = this.inputs[i];

          if (prevInput.value.trim()) {
            currentInput.disabled = false;
          } else {
            currentInput.disabled = true;
            currentInput.value = '';
            // Update counter for cleared input
            const formControl = currentInput.closest('.form-control');
            const charCountEl = formControl?.querySelector('[data-char-count]');
            if (charCountEl) {
              charCountEl.textContent = '0';
            }
          }
        }
      }

      #clearInputsAfter(inputIndex) {
        for (let i = inputIndex + 1; i < this.inputs.length; i++) {
          const currentInput = this.inputs[i];
          currentInput.disabled = true;
          currentInput.value = '';

          const formControl = currentInput.closest('.form-control');
          const charCountEl = formControl?.querySelector('[data-char-count]');
          if (charCountEl) {
            charCountEl.textContent = '0';
          }
        }

        this.#clearStoredValuesAfter(inputIndex);
      }

      #clearStoredValuesAfter(inputIndex) {
        const key = this.#getStorageKey();
        try {
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
          let changed = false;

          for (const k of Object.keys(stored)) {
            if (k === '_variantId') continue;
            if (parseInt(k, 10) > inputIndex) {
              delete stored[k];
              changed = true;
            }
          }

          if (changed) {
            sessionStorage.setItem(key, JSON.stringify(stored));
          }
        } catch {
          // Ignore errors
        }
      }

      #enforceSequentialInputs() {
        let firstEmptyIndex = null;

        for (let i = 0; i < this.inputs.length; i++) {
          if (this.inputs[i].value.trim() === '') {
            firstEmptyIndex = i;
            break;
          }
        }

        if (firstEmptyIndex === null) return;

        this.#clearInputsAfter(firstEmptyIndex);
      }

      #getFilledInputCount() {
        let count = 0;
        for (const input of this.inputs) {
          if (input.value.trim()) {
            count++;
          } else {
            break; // Stop counting at first empty input
          }
        }
        return count;
      }

      #updateNumberVariant(targetNumber, { silent = false } = {}) {
        // Target number determines variant (1 = variant "1", 5 = variant "5")
        // Minimum is 1 if there's at least one input filled / one character
        const targetValue = targetNumber > 0 ? String(targetNumber) : '1';

        const currentSelected = this.parentForm?.querySelector(
          `input[type="radio"][data-option-position="${this.dataset.optionPosition}"]:checked`,
        );
        const currentLabel = currentSelected?.nextElementSibling?.textContent?.trim();
        if (currentLabel === targetValue) return;

        for (const radio of this.radioInputs) {
          const label = radio.nextElementSibling;
          const labelText = label?.textContent?.trim();

          if (labelText === targetValue) {
            radio.checked = true;
            // Only dispatch change event if not silent - silent mode avoids DOM re-render
            if (!silent) {
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
            break;
          }
        }
      }

      #findMatchingVariant(targetNumberValue) {
        if (!this.variantPrices?.variants?.length) return null;

        // Number option position is 1-indexed, convert to 0-indexed for array access
        const numberOptionIndex = (this.variantPrices.numberOptionPosition || 1) - 1;
        const targetValue = String(targetNumberValue);

        // Get current selections for ALL options from the form
        const currentSelections = this.#getCurrentOptionSelections();

        // Find variant where:
        // - The "Number" option matches targetValue
        // - All other options match current selections
        for (const variant of this.variantPrices.variants) {
          const variantOptions = variant.options || [];

          // Check if Number option matches
          if (variantOptions[numberOptionIndex] !== targetValue) continue;

          // Check if all other options match current selections
          let allMatch = true;
          for (let i = 0; i < variantOptions.length; i++) {
            if (i === numberOptionIndex) continue; // Skip the Number option

            const optionPosition = i + 1; // Convert to 1-indexed
            const currentSelection = currentSelections[optionPosition];

            // If we have a selection for this option and it doesn't match, skip this variant
            if (currentSelection && variantOptions[i] !== currentSelection) {
              allMatch = false;
              break;
            }
          }

          if (allMatch) return variant;
        }

        return null;
      }

      #getCurrentOptionSelections() {
        // Get current selections for all options from radio inputs in the form
        // Returns object keyed by option position (1-indexed)
        const selections = {};

        if (!this.parentForm) return selections;

        // getAttribute first: the form contains <input name="id">, so form.id returns that input, not the form's id
        const formId = this.parentForm.getAttribute('id');
        if (!formId) return selections;

        // Find all checked radio inputs that belong to this form and have option-position
        const checkedRadios = document.querySelectorAll(
          `input[type="radio"][form="${formId}"][data-option-position]:checked`,
        );

        for (const radio of checkedRadios) {
          const position = parseInt(radio.dataset.optionPosition, 10);
          const label = radio.nextElementSibling;
          const labelText = label?.textContent?.trim();

          if (position && labelText) {
            selections[position] = labelText;
          }
        }

        return selections;
      }

      #updatePriceDisplay(variant) {
        if (!variant || !this.variantPrices) return;

        // Find the container - prioritize quick-buy/modal containers to find ALL price-lists
        // Quick-buy modal has separate price-lists for mobile and desktop in different branches
        const quickBuyModal = this.closest('quick-buy-modal');
        const quickBuy = this.closest('.quick-buy');
        const productContainer = quickBuyModal || quickBuy || this.closest('.product-info') || this.closest('.product');
        if (!productContainer) return;

        // Find ALL price-lists and update visible ones (quick-buy has separate mobile/desktop)
        const allPriceLists = productContainer.querySelectorAll('price-list');
        if (!allPriceLists.length) return;

        // Update ALL price-lists (both mobile and desktop in quick-buy)
        for (const priceList of allPriceLists) {
          this.#updateSinglePriceList(priceList, variant);
        }

        // Update the on-sale-badge in the product gallery
        this.#updateSaleBadge(variant, variant.compareAtPrice > variant.price);
      }

      #updateSinglePriceList(priceList, variant) {
        if (!priceList) return;

        const salePrice = priceList.querySelector('sale-price');
        const compareAtPrice = priceList.querySelector('compare-at-price');

        if (!salePrice) return;

        const isOnSale = variant.compareAtPrice > variant.price;
        const salePriceLabel = this.variantPrices.salePriceLabel || 'Sale price';
        const regularPriceLabel = this.variantPrices.regularPriceLabel || 'Regular price';

        // Preserve existing size classes (h6, h7, text-sm, etc.) - only toggle text-on-sale
        // Extract size class from current className to preserve it
        const currentClasses = salePrice.className;
        const sizeClassMatch = currentClasses.match(/\b(h[1-7]|text-(?:xs|sm|base|lg|xl|2xl|3xl))\b/);
        const salePriceSize = sizeClassMatch ? sizeClassMatch[1] : 'h6';
        // For compare-at, derive from sale price size (h6->h7, text-sm->text-sm)
        const compareAtPriceSize = salePriceSize.startsWith('h')
          ? `h${Math.min(parseInt(salePriceSize[1]) + 1, 7)}`
          : salePriceSize;

        // Update sale price content
        salePrice.innerHTML = `<span class="sr-only">${salePriceLabel}</span>${variant.priceFormatted}`;

        // Update sale price classes - preserve size, toggle on-sale state
        salePrice.className = `font-bricolage-grotesque ${salePriceSize}${isOnSale ? ' text-on-sale' : ''}`;

        // Handle compare-at price
        if (isOnSale) {
          if (compareAtPrice) {
            // Update existing compare-at price
            compareAtPrice.innerHTML = `<span class="sr-only">${regularPriceLabel}</span>${variant.compareAtPriceFormatted}`;
            compareAtPrice.className = `font-bricolage-grotesque ${compareAtPriceSize} line-through`;
          } else {
            // Create compare-at price element if it doesn't exist
            const newCompareAt = document.createElement('compare-at-price');
            newCompareAt.className = `font-bricolage-grotesque ${compareAtPriceSize} line-through`;
            newCompareAt.innerHTML = `<span class="sr-only">${regularPriceLabel}</span>${variant.compareAtPriceFormatted}`;
            salePrice.insertAdjacentElement('afterend', newCompareAt);
          }
        } else if (compareAtPrice) {
          // Remove compare-at price if not on sale
          compareAtPrice.remove();
        }
      }

      #updateSaleBadge(variant, isOnSale) {
        // Find the on-sale-badge - prioritize quick-buy containers to find ALL badges
        const quickBuyModal = this.closest('quick-buy-modal');
        const quickBuy = this.closest('.quick-buy');
        const productContainer =
          quickBuyModal || quickBuy || this.closest('.product') || this.closest('[data-product]');
        if (!productContainer) return;

        // Find ALL on-sale-badges (quick-buy has separate mobile/desktop badges)
        const allBadges = productContainer.querySelectorAll('on-sale-badge');
        if (!allBadges.length) return;

        // Update ALL badges
        for (const saleBadge of allBadges) {
          if (isOnSale) {
            // Calculate savings based on discount mode
            let savings;
            const discountMode = this.variantPrices.discountMode || 'percentage';

            if (discountMode === 'percentage') {
              const percentOff = Math.round(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100);
              savings = `${percentOff}%`;
            } else {
              // Amount mode - use pre-formatted savings (contains HTML from Shopify money formatter)
              savings = variant.savingsFormatted || '';
            }

            // Use template with placeholder replaced
            // Use innerHTML because savingsFormatted may contain HTML like <span class="money">
            const template = this.variantPrices.discountBadgeTemplate || 'Save __SAVINGS__';
            saleBadge.innerHTML = template.replace('__SAVINGS__', savings);
            saleBadge.hidden = false;
          } else {
            // Hide badge if not on sale
            saleBadge.hidden = true;
          }
        }
      }

      #saveValue(inputIndex, value) {
        const key = this.#getStorageKey();
        try {
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
          if (value) {
            stored[inputIndex] = value;
          } else {
            delete stored[inputIndex];
            for (const k of Object.keys(stored)) {
              if (parseInt(k, 10) > inputIndex) {
                delete stored[k];
              }
            }
          }
          sessionStorage.setItem(key, JSON.stringify(stored));
        } catch {
          // If parsing fails, start fresh
          if (value) {
            sessionStorage.setItem(key, JSON.stringify({ [inputIndex]: value }));
          } else {
            sessionStorage.setItem(key, JSON.stringify({}));
          }
        }
      }

      #saveVariantId() {
        const key = this.#getStorageKey();
        try {
          const stored = JSON.parse(sessionStorage.getItem(key) || '{}');
          stored._variantId = this.#getCurrentVariantId();
          sessionStorage.setItem(key, JSON.stringify(stored));
        } catch {
          // Ignore errors
        }
      }

      #restoreValues() {
        const key = this.#getStorageKey();

        // First check if stored variant ID matches current variant
        const stored = sessionStorage.getItem(key);
        if (stored) {
          try {
            const storedData = JSON.parse(stored);
            const storedVariantId = storedData._variantId;
            const currentVariantId = this.#getCurrentVariantId();

            // Clear storage if variant IDs don't match (or no stored variant ID)
            if (!storedVariantId || (currentVariantId && storedVariantId !== currentVariantId)) {
              sessionStorage.removeItem(key);
              return; // Don't restore anything
            }
          } catch {
            sessionStorage.removeItem(key);
            return;
          }
        }

        // Now restore values
        try {
          const storedData = JSON.parse(sessionStorage.getItem(key) || '{}');

          for (const input of this.inputs) {
            const inputIndex = parseInt(input.dataset.inputIndex, 10);
            const value = storedData[inputIndex];

            if (value) {
              input.value = value;

              // Update character counter
              const formControl = input.closest('.form-control');
              const charCountEl = formControl?.querySelector('[data-char-count]');
              if (charCountEl) {
                charCountEl.textContent = value.length;
              }
            }
          }

          // For Field Count mode (legacy, no dropdown), update input states after restoring
          if (!this.isCharacterMode && !this.dropdownButton) {
            this.#updateInputStates();
          }
          this.lastFilledCount = this.#getFilledInputCount();
        } catch {
          // Invalid stored data, ignore
        }
      }

      #onFormSubmit(event) {
        // Ensure variant is updated before validation in character mode
        // Use silent mode to avoid DOM re-render during form submission
        if (this.isCharacterMode) {
          const charCount = this.inputs[0]?.value?.length || 0;
          this.#updateNumberVariant(charCount, { silent: true });
          this.#syncFormVariantId(charCount);
        }

        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }

        // An add to cart is starting: hold any pending variant re-render until it has finished
        if (this.isCharacterMode) {
          this.#submitPendingUntil = Date.now() + 2500;
        }

        // In Field Count mode, disable empty inputs so they don't submit empty properties
        if (!this.isCharacterMode) {
          for (const input of this.inputs) {
            if (!input.value.trim()) {
              input.disabled = true;
            }
          }
          // Re-enable correct input states after form data has been synchronously collected.
          // Only needed for legacy field-count mode (no dropdown); dropdown mode's inputs
          // are already correctly managed by the dropdown selection.
          if (!this.dropdownButton) {
            requestAnimationFrame(() => {
              if (this.isConnected) {
                this.#updateInputStates();
              }
            });
          }
        }
      }

      #validate() {
        // Optional if Number option is explicitly set to 0
        if (this.dataset.optional === 'true' && this.#isZeroSelectedForNumberOption()) return true;

        // Dropdown/Field Count mode: all visible inputs are required
        if (this.dropdownButton) {
          for (const input of this.inputs) {
            // Skip hidden/disabled inputs (not visible based on dropdown selection)
            if (input.disabled) continue;

            const value = input?.value?.trim() || '';

            // Check if empty or doesn't meet minLength
            if (!value || (this.minLength > 0 && value.length < this.minLength)) {
              this.#showValidationError(input, this.errorMessage);
              return false;
            }

            // Check max length (safety net for Android IME bypassing HTML maxlength)
            if (value && this.maxLength > 0 && value.length > this.maxLength) {
              this.#showValidationError(input, `Please enter no more than ${this.maxLength} characters`);
              return false;
            }

            // Check pattern validity for this input
            if (value && this.pattern) {
              if (!this.#validatePattern(input, value)) {
                return false;
              }
            }
          }
          return true;
        }

        // Default: First input must have a value
        const firstInput = this.inputs[0];
        const value = firstInput?.value?.trim() || '';

        // Check if empty or doesn't meet minLength
        if (!value || (this.minLength > 0 && value.length < this.minLength)) {
          this.#showValidationError(firstInput, this.errorMessage);
          return false;
        }

        // Check max length (safety net for Android IME bypassing HTML maxlength)
        if (value && this.maxLength > 0 && value.length > this.maxLength) {
          this.#showValidationError(firstInput, `Please enter no more than ${this.maxLength} characters`);
          return false;
        }

        // Check pattern validity (validation_mode) - for accelerated checkout buttons
        // Native browser validation handles this for regular form submit via #onInvalid
        if (value && this.pattern) {
          if (!this.#validatePattern(firstInput, value)) {
            return false;
          }
        }

        return true;
      }

      #validatePattern(input, value) {
        if (this.#charTestRegex) {
          const hasInvalid = [...value].some((ch) => !this.#charTestRegex.test(ch));
          if (hasInvalid) {
            this.#showValidationError(input, this.patternMessage);
            return false;
          }
        } else if (input && !input.validity.valid && input.validity.patternMismatch) {
          this.#showValidationError(input, this.patternMessage);
          return false;
        }
        return true;
      }

      #showValidationError(input, message) {
        // Find error element relative to input's field
        const inputField = input?.closest('.text-input-overlay__field');
        const fieldErrorEl = inputField?.querySelector('[data-error-element]');

        // Turn legend/label red on error
        const legend = this.querySelector('legend.text-subdued, label.text-subdued');
        if (legend) {
          legend.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        if (input) {
          input.classList.add('text-input-overlay__input--error');

          document.dispatchEvent(
            new CustomEvent('toast:show', {
              detail: {
                message: message,
                tone: 'error',
              },
            }),
          );

          // Auto-remove border error after 3 seconds
          setTimeout(() => {
            input.classList.remove('text-input-overlay__input--error');
            if (legend) legend.style.color = '';
          }, 3000);
        }

        // Show inline error message - find error element relative to input's field first
        const errorEl = fieldErrorEl || this.querySelector('[data-error-element]');
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }
      }

      #clearError() {
        // Reset legend/label color
        const legend = this.querySelector('legend.text-subdued, label.text-subdued');
        if (legend) legend.style.color = '';

        // Clear border error from all inputs
        for (const input of this.inputs) {
          input.classList.remove('text-input-overlay__input--error');
        }

        // Clear ALL inline error messages
        const errorEls = this.querySelectorAll('[data-error-element]');
        for (const errorEl of errorEls) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }

      #showPatternHint(input) {
        const inputField = input.closest('.text-input-overlay__field');
        const hintEl = inputField?.querySelector('[data-error-element]') || this.querySelector('[data-error-element]');
        if (!hintEl) return;

        hintEl.textContent = this.patternMessage;
        hintEl.hidden = false;

        clearTimeout(this.#hintTimeout);
        this.#hintTimeout = setTimeout(() => {
          hintEl.textContent = '';
          hintEl.hidden = true;
          this.#hintTimeout = null;
        }, 2000);
      }

      // ========== Dropdown Methods (Field Count Mode) ==========

      #getDropdownStorageKey() {
        // Create a unique key for this dropdown based on product/section
        const productId = this.closest('[data-product-id]')?.dataset.productId || '';
        const sectionId = this.closest('[data-section-id]')?.dataset.sectionId || this.closest('section')?.id || '';
        return `text-input-dropdown-${productId}-${sectionId}`;
      }

      #initializeDropdown() {
        let config = { defaultIndex: 0, defaultInputCount: 1 };
        try {
          config = JSON.parse(this.dropdownConfig?.textContent || '{}');
        } catch {
          /* ignore parsing errors */
        }

        // Check if user has a stored selection (persists across Shopify re-renders within same page load)
        const storageKey = this.#getDropdownStorageKey();
        const storedData = sessionStorage.getItem(storageKey);
        let storedInputCount = null;

        // Only use stored value if it's from the current page load
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            const currentPageLoadId = getTextInputOverlayPageLoadId();
            if (parsed.pageLoadId === currentPageLoadId) {
              storedInputCount = parsed.inputCount;
            }
          } catch {
            // Invalid JSON, ignore
          }
        }

        const visibleInputs = Array.from(this.dropdownInputs);
        const alreadyCheckedInput = visibleInputs.find((input) => input.checked);

        // Priority: stored selection > already checked (URL variant) > config default
        let targetInputCount;
        if (storedInputCount) {
          targetInputCount = parseInt(storedInputCount, 10);
        } else if (alreadyCheckedInput) {
          targetInputCount = parseInt(alreadyCheckedInput.dataset.inputCount, 10);
        } else {
          targetInputCount = config.defaultInputCount;
        }

        // Find the input matching the target count
        const targetInput =
          visibleInputs.find((input) => {
            const inputCount = parseInt(input.dataset.inputCount, 10);
            return inputCount === targetInputCount;
          }) || visibleInputs[0];

        const url = new URL(window.location.href);
        const hasVariantInUrl = url.searchParams.has('variant');

        if (targetInput) {
          if (!targetInput.checked) {
            targetInput.checked = true;
          }
          // Dispatch change event on fresh page load without variant in URL
          // This syncs the URL with the dropdown's default selection
          if (!storedInputCount && !hasVariantInUrl) {
            targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // Update button text and visible inputs
        const checkedInput = this.querySelector('[data-dropdown-input]:checked');
        if (checkedInput) {
          const buttonText = this.querySelector('[data-dropdown-text]');
          if (buttonText) {
            buttonText.innerHTML = checkedInput.dataset.displayLabel;
          }
          this.#updateVisibleInputs(parseInt(checkedInput.dataset.inputCount, 10));
        }
      }

      #onDropdownChange(event) {
        const input = event.target;
        const targetCount = parseInt(input.dataset.inputCount, 10);

        // Store selection to persist across Shopify re-renders (within same page load only)
        const storageKey = this.#getDropdownStorageKey();
        const storageData = JSON.stringify({
          inputCount: targetCount.toString(),
          pageLoadId: getTextInputOverlayPageLoadId(),
        });
        sessionStorage.setItem(storageKey, storageData);

        // Update button text
        const buttonText = this.querySelector('[data-dropdown-text]');
        if (buttonText) {
          buttonText.innerHTML = input.dataset.displayLabel;
        }

        // Update visible text inputs based on selection
        this.#updateVisibleInputs(targetCount);

        // Clear any stored values for inputs that are now hidden
        this.#clearStoredValuesAfter(targetCount - 1);
      }

      #updateVisibleInputs(count) {
        // Show/hide text input fields based on dropdown selection
        // In Field Count/dropdown mode, all visible inputs are enabled (no sequential unlock)
        for (let i = 0; i < this.inputs.length; i++) {
          const field = this.inputs[i].closest('.text-input-overlay__field');
          if (!field) continue;

          if (i < count) {
            field.hidden = false;
            // In dropdown mode, enable all visible inputs immediately
            this.inputs[i].disabled = false;
          } else {
            field.hidden = true;
            this.inputs[i].value = '';
            this.inputs[i].disabled = true;

            // Update counter for cleared input
            const formControl = this.inputs[i].closest('.form-control');
            const charCountEl = formControl?.querySelector('[data-char-count]');
            if (charCountEl) {
              charCountEl.textContent = '0';
            }
          }
        }

        // Update the filled count for variant selection (skip sequential logic in dropdown mode)
        this.lastFilledCount = this.#getFilledInputCount();
      }
    },
  );
}

/**
 * Builder Text Input
 * Handles text input with min/max length validation for builder inputs
 */
if (!customElements.get('builder-text-input')) {
  customElements.define(
    'builder-text-input',
    class extends HTMLElement {
      #boundOnInput;
      #boundOnInvalid;
      #boundOnFormSubmit;
      #boundOnPaymentButtonClick;
      #charTestRegex;
      #hintTimeout;

      connectedCallback() {
        this.input = this.querySelector('input[type="text"]');
        this.errorElement = this.querySelector('[data-error-message]');
        this.charCounter = this.querySelector('[data-char-counter]');
        this.patternMessage = this.dataset.patternMessage || 'Invalid format';
        this.lengthMessage = this.dataset.lengthMessage || '';
        this.requiredMessage = this.dataset.requiredMessage;
        // Ensure requiredMessage is never undefined or empty
        if (!this.requiredMessage || this.requiredMessage === 'undefined') {
          this.requiredMessage = 'This field is required';
        }
        this.minLength = parseInt(this.dataset.minLength, 10) || 0;
        this.maxLength = parseInt(this.dataset.maxLength, 10) || 0;
        this.isRequired = this.dataset.required === 'true';
        this.textTransform = this.dataset.textTransform || 'none';

        if (!this.input) return;

        this.#charTestRegex = compileCharFilterRegex(this.input.pattern);

        // Register with coordinated validation system
        window.builderValidation?.register(this);

        // Fallback: read min/max length from input element if not on wrapper
        if (this.minLength === 0 && this.input.minLength > 0) {
          this.minLength = this.input.minLength;
        }
        if (this.maxLength === 0 && this.input.maxLength > 0) {
          this.maxLength = this.input.maxLength;
        }

        // Clear any lingering error state
        this.input.classList.remove('input--error');
        this.#hideError();

        // Initialize character counter
        this.#updateCharCounter();

        // Bind input event for real-time validation
        this.#boundOnInput = this.#onInput.bind(this);
        this.input.addEventListener('input', this.#boundOnInput);

        // Intercept native browser validation to show custom toast instead
        // Use capture phase to run before native validation handling
        this.#boundOnInvalid = this.#onInvalid.bind(this);
        this.input.addEventListener('invalid', this.#boundOnInvalid, true);

        // Listen for form submission to validate
        this.parentForm = this.#findParentForm();
        if (this.parentForm) {
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.parentForm.addEventListener('submit', this.#boundOnFormSubmit, true);
        }

        // Intercept accelerated checkout button clicks
        this.#setupPaymentButtonValidation();
      }

      disconnectedCallback() {
        window.builderValidation?.unregister(this);

        this.input?.removeEventListener('input', this.#boundOnInput);
        this.input?.removeEventListener('invalid', this.#boundOnInvalid, true);
        this.parentForm?.removeEventListener('submit', this.#boundOnFormSubmit, true);

        // Clean up payment button listener
        if (this.#boundOnPaymentButtonClick) {
          document.removeEventListener('click', this.#boundOnPaymentButtonClick, true);
        }

        clearTimeout(this.#hintTimeout);
      }

      // Public method for coordinated validation
      getAssociatedProductForm() {
        return this.parentForm?.closest('product-form') || this.closest('product-form');
      }

      // Public method for coordinated validation - validates with feedback
      validateWithFeedback() {
        return this.#validate();
      }

      #setupPaymentButtonValidation() {
        // Use document-level delegation to handle dynamically replaced payment buttons
        this.#boundOnPaymentButtonClick = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#boundOnPaymentButtonClick, true);
      }

      #onPaymentButtonClick(event) {
        // Check if click is within a shopify payment button
        const paymentButton = event.target.closest('.shopify-payment-button');
        if (!paymentButton) return;

        // Check if this payment button is associated with our form
        const clickedProductForm = paymentButton.closest('product-form');
        const ourProductForm = this.getAssociatedProductForm();

        if (clickedProductForm !== ourProductForm) return;

        // Use coordinated validation to ensure DOM order
        if (!window.builderValidation.validateForForm(ourProductForm)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }

      #validate() {
        const value = this.input.value.trim();
        const length = value.length;

        // Check if required but empty
        if (this.isRequired && !value) {
          this.#showValidationError(this.requiredMessage);
          return false;
        }

        // Check pattern validity
        if (value && !this.input.validity.valid && this.input.validity.patternMismatch) {
          this.#showValidationError(this.patternMessage);
          return false;
        }

        // Check min length
        if (value && this.minLength > 0 && length < this.minLength) {
          this.#showValidationError(this.lengthMessage || `Minimum ${this.minLength} characters required`);
          return false;
        }

        // Check max length (safety net for Android IME bypassing HTML maxlength)
        if (value && this.maxLength > 0 && length > this.maxLength) {
          this.#showValidationError(this.lengthMessage || `Maximum ${this.maxLength} characters allowed`);
          return false;
        }

        return true;
      }

      #findParentForm() {
        const formId = this.input?.getAttribute('form');
        if (formId) return document.getElementById(formId);
        return this.closest('form');
      }

      #onInput() {
        // Apply text transformation if configured
        if (this.textTransform !== 'none') {
          const cursorPos = this.input.selectionStart;
          const originalValue = this.input.value;
          const transformedValue = applyTextTransform(originalValue, this.textTransform);

          if (transformedValue !== originalValue) {
            this.input.value = transformedValue;
            this.input.setSelectionRange(cursorPos, cursorPos);
          }
        }

        // Enforce max length in JS (Android IME can bypass the HTML maxlength attribute)
        if (this.maxLength > 0 && this.input.value.length > this.maxLength) {
          this.input.value = this.input.value.substring(0, this.maxLength);
        }

        // Strip invalid characters in real-time based on validation pattern
        const charsWereFiltered = filterInputByPattern(this.input, this.#charTestRegex);

        const value = this.input.value;
        const length = value.length;

        // Update character counter
        this.#updateCharCounter();

        // Clear error state on input
        this.input.classList.remove('input--error');
        this.#hideError();

        // Show brief informational hint when invalid characters were stripped
        if (charsWereFiltered) {
          this.#showPatternHint();
        }

        // Check length constraints
        if (value && this.minLength > 0 && length < this.minLength) {
          this.#showError(this.lengthMessage);
          return;
        }
      }

      #updateCharCounter() {
        if (!this.charCounter || this.maxLength <= 0) return;
        const currentLength = this.input.value.length;
        this.charCounter.textContent = `${currentLength}/${this.maxLength}`;
      }

      #onInvalid(event) {
        // Prevent native browser validation popup completely
        event.preventDefault();
        event.stopImmediatePropagation();

        // Determine the appropriate error message based on validity state
        const validity = this.input.validity;
        let message;

        if (validity.valueMissing) {
          message = this.requiredMessage || 'This field is required';
        } else if (validity.patternMismatch) {
          message = this.patternMessage || 'Invalid format';
        } else if (validity.tooShort) {
          message = this.lengthMessage || `Minimum ${this.minLength} characters required`;
        } else {
          message = 'Please check your input';
        }

        // Show our custom validation error
        this.#showValidationError(message);
      }

      #onFormSubmit(event) {
        // Skip validation for ghost instances left over from Shopify re-renders
        if (!this.isConnected) return;
        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }

      #showValidationError(message) {
        // Turn label red on error
        const label = this.querySelector('label.text-subdued, legend.text-subdued');
        if (label) {
          label.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        // Show toast
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: message || 'Please check your input',
              tone: 'error',
            },
          }),
        );

        // Add error styling with shake
        this.input.classList.add('input--error');

        // Show inline error message
        this.#showError(message);

        // Remove error class after animation
        setTimeout(() => {
          this.input.classList.remove('input--error');
          if (label) label.style.color = '';
        }, 3000);
      }

      #showError(message) {
        if (this.errorElement && message) {
          this.errorElement.textContent = message;
          this.errorElement.hidden = false;
        }
      }

      #hideError() {
        if (this.errorElement) {
          this.errorElement.textContent = '';
          this.errorElement.hidden = true;
        }
        // Reset label color
        const label = this.querySelector('label.text-subdued, legend.text-subdued');
        if (label) label.style.color = '';
      }

      #showPatternHint() {
        if (!this.errorElement) return;

        this.errorElement.textContent = this.patternMessage;
        this.errorElement.hidden = false;

        clearTimeout(this.#hintTimeout);
        this.#hintTimeout = setTimeout(() => {
          this.errorElement.textContent = '';
          this.errorElement.hidden = true;
          this.#hintTimeout = null;
        }, 2000);
      }
    },
  );
}

/**
 * Builder Select
 * Handles select dropdown with popover for builder inputs
 */
window.builderSelectState = window.builderSelectState || {};

if (!customElements.get('builder-select')) {
  customElements.define(
    'builder-select',
    class extends HTMLElement {
      #boundOnFormSubmit;

      connectedCallback() {
        this.buttonText = this.querySelector('[data-button-text]');
        this.selectButton = this.querySelector('[data-select-button]');
        this.errorEl = this.querySelector('[data-error-message]');
        this.form =
          this.closest('form') || document.querySelector(`form#${this.querySelector('input')?.getAttribute('form')}`);
        this.isRequired = this.dataset.required === 'true';
        this.placeholder = this.dataset.placeholder || '';

        // Register with coordinated validation system
        window.builderValidation?.register(this);

        // Clear error when user interacts with the component
        const clearErrorHandler = () => this.#clearError();
        this.addEventListener('mousedown', clearErrorHandler);
        this.addEventListener('focusin', clearErrorHandler);
        if (this.selectButton) {
          this.selectButton.addEventListener('click', clearErrorHandler);
        }

        const popoverId = this.querySelector('button[aria-controls]')?.getAttribute('aria-controls');
        if (!popoverId) return;

        const firstInput = this.querySelector('input[type="radio"]');
        this.inputName = firstInput?.name;

        if (this.inputName && window.builderSelectState[this.inputName]) {
          const savedValue = window.builderSelectState[this.inputName];
          const input = this.querySelector(`input[value="${CSS.escape(savedValue)}"]`);

          if (input) {
            input.checked = true;

            if (this.buttonText) {
              this.buttonText.textContent = savedValue;
            }
          }
        }

        const popover = document.getElementById(popoverId);
        if (!popover) return;

        popover.addEventListener('change', (event) => {
          const input = event.target;
          if (input.type !== 'radio') return;

          const selectedValue = input.value;

          if (input.name) {
            window.builderSelectState[input.name] = selectedValue;
          }

          if (this.buttonText) {
            this.buttonText.textContent = selectedValue;
          }

          this.#clearError();
        });

        // Form submission validation
        if (this.form) {
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.form.addEventListener('submit', this.#boundOnFormSubmit);
        }

        // Intercept native invalid event on radio inputs
        const inputs = this.querySelectorAll('input[type="radio"]');

        for (const input of inputs) {
          input.addEventListener('invalid', this.#onInvalid.bind(this), true);
        }

        // Payment button validation via document-level event delegation
        this.#paymentClickHandler = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#paymentClickHandler, true);
      }

      disconnectedCallback() {
        window.builderValidation?.unregister(this);

        if (this.#boundOnFormSubmit && this.form) {
          this.form.removeEventListener('submit', this.#boundOnFormSubmit);
        }

        if (this.#paymentClickHandler) {
          document.removeEventListener('click', this.#paymentClickHandler, true);
        }
      }

      // Public method for coordinated validation
      getAssociatedProductForm() {
        return this.form?.closest('product-form');
      }

      // Public method for coordinated validation - validates with feedback
      validateWithFeedback() {
        return this.#validate();
      }

      #paymentClickHandler = null;

      #onPaymentButtonClick(event) {
        const paymentButton = event.target.closest?.('.shopify-payment-button');
        if (!paymentButton) return;

        // Find our associated product-form via the form element
        const ourProductForm = this.getAssociatedProductForm();
        if (!ourProductForm) return;

        const buttonProductForm = paymentButton.closest('product-form');
        if (buttonProductForm !== ourProductForm) return;

        // Use coordinated validation to ensure DOM order
        if (!window.builderValidation.validateForForm(ourProductForm)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      }

      #onFormSubmit(event) {
        // Skip validation for ghost instances left over from Shopify re-renders
        if (!this.isConnected) return;
        if (!this.#validate()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      #onInvalid(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#validate();
      }

      #validate() {
        if (!this.isRequired) return true;

        const checkedInput = this.querySelector('input[type="radio"]:checked');

        if (!checkedInput) {
          this.#showError(this.dataset.requiredMessage || 'Please make a selection');
          return false;
        }

        this.#clearError();
        return true;
      }

      #showError(message) {
        // Turn legend red on error
        const legend = this.querySelector('legend.text-subdued');
        if (legend) {
          legend.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        const button = this.querySelector('[data-select-button]');
        if (button) {
          button.classList.add('input--error');
          button.classList.add('shake');
          setTimeout(() => button.classList.remove('shake'), 500);

          // Auto-remove border error class after 3 seconds
          setTimeout(() => {
            button.classList.remove('input--error');
            if (legend) legend.style.color = '';
          }, 3000);
        }

        const errorEl = this.querySelector('[data-error-message]');
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }

        window.showToast?.(message, 'error');
      }

      #clearError() {
        // Reset legend color
        const legend = this.querySelector('legend.text-subdued');
        if (legend) legend.style.color = '';

        const button = this.querySelector('[data-select-button]');
        if (button) {
          button.classList.remove('input--error');
        }

        const errorEl = this.querySelector('[data-error-message]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }
    },
  );
}

/**
 * Builder Upload
 * Handles image upload with preview for builder inputs
 */
if (!customElements.get('builder-upload')) {
  customElements.define(
    'builder-upload',
    class extends HTMLElement {
      #boundOnPaymentButtonClick;
      #boundOnFormSubmit;

      connectedCallback() {
        this.input = this.querySelector('.builder-upload__input');
        this.label = this.querySelector('.builder-upload__label');
        this.textEl = this.querySelector('.builder-upload__text');
        this.preview = this.querySelector('.builder-upload__preview');
        this.previewImage = this.querySelector('.builder-upload__preview-image');
        this.removeBtn = this.querySelector('.builder-upload__remove');
        this.uploadContainer = this.querySelector('[data-upload-container]');
        this.uploadLabel = this.querySelector('[data-upload-label]');
        this.errorEl = this.querySelector('[data-error-message]');
        this.defaultText = this.textEl?.dataset.defaultText || 'Choose file';
        this.isRequired = this.dataset.required === 'true';
        this.requiredMessage = this.dataset.requiredMessage || 'This field is required';

        // Find associated form
        const formId = this.input?.getAttribute('form');
        this.parentForm = this.closest('form') || (formId && document.getElementById(formId));

        // Register with coordinated validation system
        window.builderValidation?.register(this);

        if (this.input) {
          this.input.addEventListener('change', this.#onFileChange.bind(this));
          this.input.addEventListener('invalid', this.#onInvalid.bind(this), true);
        }

        if (this.removeBtn) {
          this.removeBtn.addEventListener('click', this.#onRemove.bind(this));
        }

        // Clear error when user interacts with the component
        const clearErrorHandler = () => this.#clearError();
        this.addEventListener('mousedown', clearErrorHandler);
        this.addEventListener('focusin', clearErrorHandler);
        if (this.uploadLabel) {
          this.uploadLabel.addEventListener('click', clearErrorHandler);
        }

        // Form submission validation
        if (this.parentForm) {
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.parentForm.addEventListener('submit', this.#boundOnFormSubmit, true);
        }

        // Payment button validation via document-level event delegation
        this.#boundOnPaymentButtonClick = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#boundOnPaymentButtonClick, true);
      }

      disconnectedCallback() {
        window.builderValidation?.unregister(this);

        if (this.#boundOnFormSubmit && this.parentForm) {
          this.parentForm.removeEventListener('submit', this.#boundOnFormSubmit, true);
        }

        if (this.#boundOnPaymentButtonClick) {
          document.removeEventListener('click', this.#boundOnPaymentButtonClick, true);
        }
      }

      // Public method for coordinated validation
      getAssociatedProductForm() {
        return this.parentForm?.closest('product-form') || this.closest('product-form');
      }

      // Public method for coordinated validation - validates with feedback
      validateWithFeedback() {
        return this.#validate();
      }

      #onPaymentButtonClick(event) {
        const paymentButton = event.target.closest?.('.shopify-payment-button');
        if (!paymentButton) return;

        // Find our associated product-form
        const ourProductForm = this.getAssociatedProductForm();
        if (!ourProductForm) return;

        const buttonProductForm = paymentButton.closest('product-form');
        if (buttonProductForm !== ourProductForm) return;

        // Use coordinated validation to ensure DOM order
        if (!window.builderValidation.validateForForm(ourProductForm)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      }

      #onFormSubmit(event) {
        // Skip validation for ghost instances left over from Shopify re-renders
        if (!this.isConnected) return;
        if (!this.#validate()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      #onInvalid(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#validate();
      }

      #validate() {
        if (!this.isRequired) return true;

        const hasFile = this.input?.files?.length > 0;

        if (!hasFile) {
          this.#showError(this.requiredMessage);
          return false;
        }

        this.#clearError();
        return true;
      }

      #showError(message) {
        // Turn field label red on error
        const fieldLabel = this.querySelector('label.text-subdued');
        if (fieldLabel) {
          fieldLabel.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        const label = this.querySelector('[data-upload-label]');
        if (label) {
          label.classList.add('input--error');
          label.classList.add('shake');
          setTimeout(() => label.classList.remove('shake'), 500);

          // Auto-remove border error class after 3 seconds
          setTimeout(() => {
            label.classList.remove('input--error');
            if (fieldLabel) fieldLabel.style.color = '';
          }, 3000);
        }

        const errorEl = this.querySelector('[data-error-message]');
        if (errorEl) {
          errorEl.textContent = message;
          errorEl.hidden = false;
        }

        window.showToast?.(message, 'error');
      }

      #clearError() {
        // Reset field label color
        const fieldLabel = this.querySelector('label.text-subdued');
        if (fieldLabel) fieldLabel.style.color = '';

        const label = this.querySelector('[data-upload-label]');
        if (label) {
          label.classList.remove('input--error');
        }

        const errorEl = this.querySelector('[data-error-message]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }

      #onFileChange(event) {
        const file = event.target.files[0];

        // Clear error when file is selected
        this.#clearError();

        if (!file) return;

        if (this.textEl) {
          this.textEl.textContent = file.name;
        }

        if (this.uploadContainer) {
          this.uploadContainer.classList.add('builder-upload--has-file');
        }

        if (file.type.startsWith('image/') && this.preview && this.previewImage) {
          const reader = new FileReader();

          reader.onload = (e) => {
            this.previewImage.src = e.target.result;
            this.preview.hidden = false;
          };

          reader.readAsDataURL(file);
        }
      }

      #onRemove() {
        if (this.input) {
          this.input.value = '';
        }

        if (this.textEl) {
          this.textEl.textContent = this.defaultText;
        }

        if (this.uploadContainer) {
          this.uploadContainer.classList.remove('builder-upload--has-file');
        }

        if (this.preview) {
          this.preview.hidden = true;
        }

        if (this.previewImage) {
          this.previewImage.src = 'data:,';
        }
      }
    },
  );
}

/**
 * Builder Charm Selector
 * Handles charm selection with modal for builder inputs (non-price-modifying)
 * Single selection, Popup mode only
 */
window.builderCharmState = window.builderCharmState || {};

if (!customElements.get('builder-charm-selector')) {
  customElements.define(
    'builder-charm-selector',
    class extends HTMLElement {
      #boundOnPaymentButtonClick;
      #boundOnInlineClick;
      #boundOnFormSubmit;
      #boundOnModalClick;

      connectedCallback() {
        this.propertyInput = this.querySelector('[data-property-input]');
        this.placeholderText = this.dataset.placeholderText;
        this.maxChars = parseInt(this.dataset.maxChars, 10) || 20;
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.sectionId;
        this.isRequired = this.dataset.required === 'true';
        this.errorMessage = this.dataset.errorMessage || 'Please make a selection';
        this.requiredMessage = this.dataset.requiredMessage || 'This field is required';
        this.isInlineGridMode = (this.dataset.pickerStyle || '').trim() === 'Inline Grid';

        // Popup mode elements
        this.pill = this.querySelector('[data-charm-pill]');
        this.pillText = this.querySelector('[data-pill-text]');

        // Get the drawer element from pill (Popup mode only)
        const modalId = this.pill?.getAttribute('aria-controls');
        this.modal = modalId ? document.getElementById(modalId) : null;

        // Find associated form
        const formId = this.propertyInput?.getAttribute('form');
        this.form = this.closest('form') || (formId && document.getElementById(formId));

        // Storage key for persistence
        this.storageKey = `builder-charm-${this.productId}-${this.sectionId}-${this.id}`;

        this._rerenderGuard = () => {
          const block = this.closest('[data-block-type][data-block-allow-rerender]');
          if (block) {
            block.removeAttribute('data-block-allow-rerender');
            queueMicrotask(() => block.setAttribute('data-block-allow-rerender', ''));
          }
        };
        document.addEventListener('product:rerender', this._rerenderGuard, true);

        // Register with coordinated validation system
        window.builderValidation?.register(this);

        // Setup event listeners
        this.#setupEventListeners();

        // Restore saved selection
        this.#restoreSelection();
      }

      disconnectedCallback() {
        window.builderValidation?.unregister(this);

        if (this._rerenderGuard) {
          document.removeEventListener('product:rerender', this._rerenderGuard, true);
        }

        if (this.#boundOnModalClick && this.modal) {
          this.modal.removeEventListener('click', this.#boundOnModalClick);
        }

        if (this.modal?.parentElement === document.body) {
          this.modal.remove();
        }

        if (this.#boundOnFormSubmit && this.form) {
          this.form.removeEventListener('submit', this.#boundOnFormSubmit);
        }

        if (this.#boundOnPaymentButtonClick) {
          document.removeEventListener('click', this.#boundOnPaymentButtonClick, true);
        }

        if (this.#boundOnInlineClick) {
          this.removeEventListener('click', this.#boundOnInlineClick);
        }
      }

      // Public method for coordinated validation
      getAssociatedProductForm() {
        return this.form?.closest('product-form');
      }

      // Public method for coordinated validation - validates with feedback
      validateWithFeedback() {
        return this.#validate();
      }

      // Public method to check validity without showing errors
      isValid() {
        if (!this.isRequired) return true;
        return this.propertyInput?.value?.trim() !== '';
      }

      #setupEventListeners() {
        if (this.isInlineGridMode) {
          // Inline Grid mode: bind click on charm option buttons
          this.#boundOnInlineClick = this.#onInlineClick.bind(this);
          this.addEventListener('click', this.#boundOnInlineClick);
        } else {
          // Popup mode: pill click + modal
          if (this.pill) {
            this.pill.addEventListener('click', () => {
              this.#clearError();
              this.#highlightSelectedOption();
            });
          }

          if (this.modal) {
            this.#boundOnModalClick = (e) => {
              if (!this.isConnected) return;
              const charmOption = e.target.closest('[data-charm-option]');
              if (charmOption) {
                this.#onCharmSelect(charmOption);
              }
            };
            this.modal.addEventListener('click', this.#boundOnModalClick);
          }
        }

        // Form submission validation
        if (this.form) {
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.form.addEventListener('submit', this.#boundOnFormSubmit);
        }

        // Payment button validation via document-level event delegation
        this.#boundOnPaymentButtonClick = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#boundOnPaymentButtonClick, true);
      }

      #onPaymentButtonClick(event) {
        const paymentButton = event.target.closest?.('.shopify-payment-button');
        if (!paymentButton) return;

        const ourProductForm = this.getAssociatedProductForm();
        if (!ourProductForm) return;

        const buttonProductForm = paymentButton.closest('product-form');
        if (buttonProductForm !== ourProductForm) return;

        // Use coordinated validation to ensure DOM order
        if (!window.builderValidation.validateForForm(ourProductForm)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
        }
      }

      #onFormSubmit(event) {
        // Skip validation for ghost instances left over from Shopify re-renders
        if (!this.isConnected) return;
        if (!this.#validate()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }

      #onCharmSelect(charmOption) {
        const value = charmOption.dataset.value;
        if (!value) return;

        // Update selected state visually in modal
        const allOptions = this.modal?.querySelectorAll('[data-charm-option]') || [];
        for (const opt of allOptions) {
          opt.classList.toggle('charm-selector__option--selected', opt === charmOption);
        }

        // Update property input
        if (this.propertyInput) {
          this.propertyInput.value = value;
        }

        const imageInput = this.querySelector('[data-image-input]');
        if (imageInput) imageInput.value = charmOption.dataset.image || '';

        // Update pill display
        this.#updatePillDisplay(value);

        // Save to storage
        this.#saveSelection(value);

        // Close modal
        if (this.modal?.hide) {
          this.modal.hide();
        }

        // Clear any error state
        this.#clearError();

        // Dispatch change event for stickyATCManager
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // ========== Inline Grid Methods ==========

      #onInlineClick(event) {
        const charmBtn = event.target.closest?.('[data-inline-charm-option]');
        if (charmBtn && this.contains(charmBtn)) {
          event.preventDefault();
          this.#onInlineCharmSelect(charmBtn);
        }
      }

      #onInlineCharmSelect(button) {
        const value = button.dataset.value;
        if (!value) return;

        // Toggle selected state: deselect if already selected, otherwise select new
        const allOptions = this.querySelectorAll('[data-inline-charm-option]');
        const wasSelected = button.classList.contains('charm-inline__option--selected');
        const imageInput = this.querySelector('[data-image-input]');

        for (const btn of allOptions) {
          btn.classList.remove('charm-inline__option--selected');
        }

        if (wasSelected) {
          // Deselect
          if (this.propertyInput) this.propertyInput.value = '';
          if (imageInput) imageInput.value = '';
          sessionStorage.removeItem(this.storageKey);
          delete window.builderCharmState[this.storageKey];
        } else {
          // Select new
          button.classList.add('charm-inline__option--selected');
          if (this.propertyInput) this.propertyInput.value = value;
          if (imageInput) imageInput.value = button.dataset.image || '';
          this.#saveSelection(value);
        }

        // Clear any error state
        this.#clearError();

        // Dispatch change event for stickyATCManager
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }

      #updatePillDisplay(value) {
        if (!this.pillText) return;

        if (value) {
          // Truncate if needed
          const displayText = value.length > this.maxChars ? value.slice(0, this.maxChars) + '...' : value;
          this.pillText.textContent = displayText;
          this.pill?.classList.add('charm-selector__pill--has-value');

          // Add remove button if not exists
          if (!this.pill?.querySelector('[data-charm-remove]')) {
            const removeBtn = document.createElement('span');
            removeBtn.className = 'charm-selector__remove';
            removeBtn.setAttribute('data-charm-remove', '');
            removeBtn.setAttribute('aria-label', 'Clear selection');
            removeBtn.innerHTML = `
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="7.5" cy="7.5" r="7" transform="matrix(-1 0 0 1 15 0)" fill="white" stroke="black"/>
                <path d="M9.75 9.75L5.25 5.25M9.75 5.25L5.25 9.75" stroke="black" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            `;
            removeBtn.addEventListener('click', (e) => {
              e.stopPropagation();
              this.#clearSelection();
            });
            this.pill?.appendChild(removeBtn);
          }
        } else {
          this.pillText.textContent = this.placeholderText;
          this.pill?.classList.remove('charm-selector__pill--has-value');

          // Remove the remove button
          const removeBtn = this.pill?.querySelector('[data-charm-remove]');
          removeBtn?.remove();
        }
      }

      #highlightSelectedOption() {
        if (!this.modal) return;

        const currentValue = this.propertyInput?.value?.trim();
        const allOptions = this.modal.querySelectorAll('[data-charm-option]');

        // Clear all selected states first
        for (const opt of allOptions) {
          opt.classList.remove('charm-selector__option--selected');
        }

        // Add selected state to current value
        if (currentValue) {
          for (const opt of allOptions) {
            if (opt.dataset.value === currentValue) {
              opt.classList.add('charm-selector__option--selected');
              break;
            }
          }
        }
      }

      #clearSelection() {
        // Clear property input
        if (this.propertyInput) {
          this.propertyInput.value = '';
        }

        // Update pill display
        this.#updatePillDisplay(null);

        // Clear from storage
        sessionStorage.removeItem(this.storageKey);
        delete window.builderCharmState[this.storageKey];

        // Dispatch change event for stickyATCManager
        this.dispatchEvent(new Event('change', { bubbles: true }));
      }

      #saveSelection(value) {
        try {
          sessionStorage.setItem(this.storageKey, value);
          window.builderCharmState[this.storageKey] = value;
        } catch {
          // Storage not available
        }
      }

      #restoreSelection() {
        try {
          // Try memory first
          let value = window.builderCharmState[this.storageKey];

          // Fall back to sessionStorage
          if (!value) {
            value = sessionStorage.getItem(this.storageKey);
          }

          if (value) {
            // Update property input
            if (this.propertyInput) {
              this.propertyInput.value = value;
            }

            const imageInput = this.querySelector('[data-image-input]');

            if (this.isInlineGridMode) {
              // Highlight the matching inline grid option
              const btn = Array.from(this.querySelectorAll('[data-inline-charm-option]')).find(
                (b) => (b.dataset.value || '').trim() === value,
              );
              if (btn) {
                btn.classList.add('charm-inline__option--selected');
                if (imageInput) imageInput.value = btn.dataset.image || '';
              }
            } else {
              // Update pill display
              this.#updatePillDisplay(value);
              if (imageInput) {
                const charmBtn = this.modal?.querySelector(`[data-charm-option][data-value="${CSS.escape(value)}"]`);
                if (charmBtn) imageInput.value = charmBtn.dataset.image || '';
              }
            }
          }
        } catch {
          // Storage not available
        }
      }

      #validate() {
        if (!this.isRequired) return true;

        const value = this.propertyInput?.value?.trim();

        if (!value) {
          this.#showError(this.requiredMessage);
          return false;
        }

        this.#clearError();
        return true;
      }

      #showError(message) {
        // Turn legend text red on error
        const legend = this.querySelector('legend');
        if (legend) {
          legend.style.color = 'rgb(var(--error-text, 220 38 38))';
        }

        if (this.isInlineGridMode) {
          // Same pattern as charm-inline__slot--error: highlight legend + shake
          const fieldset = this.querySelector('fieldset');
          if (fieldset) {
            fieldset.classList.add('builder-charm--inline-error');
            setTimeout(() => {
              fieldset.classList.remove('builder-charm--inline-error');
              if (legend) legend.style.color = '';
            }, 3000);
          }
        } else if (this.pill) {
          // Add error class to pill (same as price modifier charm selector)
          this.pill.classList.add('charm-selector__pill--error');

          // Inline fallback so the error is always visible
          this.pill.style.borderColor = 'rgb(var(--error-text, 220 38 38))';
          this.pill.style.animation = 'none';
          void this.pill.offsetHeight; // restart animation
          this.pill.style.animation = 'charm-pill-shake 0.4s ease-in-out';

          // Auto-remove error state after 3 seconds
          setTimeout(() => {
            this.pill.classList.remove('charm-selector__pill--error');
            this.pill.style.borderColor = '';
            this.pill.style.animation = '';
            if (legend) legend.style.color = '';
          }, 3000);
        }

        // Show error toast (no inline error text - same as price modifier)
        document.dispatchEvent(
          new CustomEvent('toast:show', {
            detail: {
              message: message,
              tone: 'error',
            },
          }),
        );
      }

      #clearError() {
        const legend = this.querySelector('legend');
        if (legend) legend.style.color = '';

        if (this.isInlineGridMode) {
          const fieldset = this.querySelector('fieldset');
          if (fieldset) fieldset.classList.remove('builder-charm--inline-error');
        } else if (this.pill) {
          this.pill.classList.remove('charm-selector__pill--error');
          this.pill.style.borderColor = '';
          this.pill.style.animation = '';
        }
      }
    },
  );
}

/**
 * Grouped Input Overlay
 * Renders N paired input groups (select + text + charm) driven by a "Number of X" variant option.
 * Each slot contains a select dropdown for the primary choice, a text input for a companion value, and a charm selector for an optional choice.
 */
if (!customElements.get('grouped-input-overlay')) {
  const getGroupedOverlayPageLoadId = (() => {
    let id = null;
    return () => {
      if (!id) id = `gl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      return id;
    };
  })();

  customElements.define(
    'grouped-input-overlay',
    class extends HTMLElement {
      static ERROR_DISPLAY_MS = 3000;

      static VALIDATION_PATTERNS = {
        english: /[^A-Za-z ]/g,
        arabic: /[^\u0600-\u06FF ]/g,
        alphanumeric: /[^A-Za-z0-9 ]/g,
      };

      #boundOnSelectChange;
      #boundOnTextInput;
      #boundOnCharmOptionClick;
      #boundOnCharmRemoveClick;
      #boundOnFormSubmit;
      #boundOnPaymentButtonClick;
      #boundOnDropdownChange;

      connectedCallback() {
        this.fieldConfig = [];
        try {
          this.fieldConfig = JSON.parse(this.querySelector('[data-field-config]')?.textContent || '[]');
        } catch {
          /* ignore */
        }

        this.slots = this.querySelectorAll('.grouped-input-overlay__slot');
        this.variantRadios = this.querySelectorAll('.sr-only input[type="radio"]');
        this.productId = this.dataset.productId;
        this.sectionId = this.dataset.sectionId;
        this.errorMessage = this.dataset.errorMessage || 'Please make a selection';
        this.requiredMessage = this.dataset.requiredMessage || 'This field is required';

        this.dropdownButton = this.querySelector('[data-dropdown-button]');
        this.dropdownInputs = this.querySelectorAll('[data-dropdown-input]');
        this.dropdownConfigEl = this.querySelector('[data-dropdown-config]');

        this.#migrateStorageFormat();

        if (this.dropdownButton && this.dropdownInputs.length > 0) {
          this.#initializeDropdown();
          this.#boundOnDropdownChange = this.#onDropdownChange.bind(this);
          for (const input of this.dropdownInputs) {
            input.addEventListener('change', this.#boundOnDropdownChange);
          }
        } else {
          this.#restoreSelections();
        }

        this.#boundOnSelectChange = this.#onSelectChange.bind(this);
        for (const input of this.querySelectorAll('[data-select-input]')) {
          input.addEventListener('change', this.#boundOnSelectChange);
        }

        this.#boundOnTextInput = this.#onTextInput.bind(this);
        for (const input of this.querySelectorAll('[data-text-input]')) {
          input.addEventListener('input', this.#boundOnTextInput);
        }

        this.#boundOnCharmOptionClick = this.#onCharmOptionClick.bind(this);
        for (const btn of this.querySelectorAll('[data-charm-option]')) {
          btn.addEventListener('click', this.#boundOnCharmOptionClick);
        }

        this.#boundOnCharmRemoveClick = this.#onCharmRemoveClick.bind(this);
        for (const btn of this.querySelectorAll('[data-charm-remove]')) {
          btn.addEventListener('click', this.#boundOnCharmRemoveClick);
        }

        window.builderValidation?.register(this);

        this.parentForm = this.#findParentForm();
        if (this.parentForm) {
          this.#boundOnFormSubmit = this.#onFormSubmit.bind(this);
          this.parentForm.addEventListener('submit', this.#boundOnFormSubmit, true);
        }

        this.#setupPaymentButtonValidation();
      }

      disconnectedCallback() {
        window.builderValidation?.unregister(this);

        for (const input of this.querySelectorAll('[data-select-input]')) {
          input.removeEventListener('change', this.#boundOnSelectChange);
        }
        for (const input of this.querySelectorAll('[data-text-input]')) {
          input.removeEventListener('input', this.#boundOnTextInput);
        }
        for (const btn of this.querySelectorAll('[data-charm-option]')) {
          btn.removeEventListener('click', this.#boundOnCharmOptionClick);
        }
        for (const btn of this.querySelectorAll('[data-charm-remove]')) {
          btn.removeEventListener('click', this.#boundOnCharmRemoveClick);
        }
        if (this.#boundOnDropdownChange) {
          for (const input of this.dropdownInputs) {
            input.removeEventListener('change', this.#boundOnDropdownChange);
          }
        }
        if (this.parentForm && this.#boundOnFormSubmit) {
          this.parentForm.removeEventListener('submit', this.#boundOnFormSubmit, true);
        }
        if (this.#boundOnPaymentButtonClick) {
          document.removeEventListener('click', this.#boundOnPaymentButtonClick, true);
        }
      }

      validateWithFeedback() {
        const isValid = this.#validate();
        if (!isValid) this.#showErrors();
        return isValid;
      }

      getAssociatedProductForm() {
        return this.parentForm?.closest('product-form') || this.closest('product-form');
      }

      // ========== Private Helpers ==========

      #findParentForm() {
        const firstInput = this.querySelector('[data-select-input], [data-text-input], [data-charm-input]');
        const formId = firstInput?.getAttribute('form');
        return this.closest('form') || (formId && document.getElementById(formId));
      }

      #isZeroSelectedForNumberOption() {
        const numberOptionPosition = this.dataset.numberOptionPosition;
        if (!numberOptionPosition) return true;
        const parentForm = this.parentForm || this.#findParentForm();
        if (!parentForm) return false;
        const numberRadio = parentForm.querySelector(
          `input[type="radio"][data-option-position="${numberOptionPosition}"]:checked`,
        );
        const labelText = numberRadio?.nextElementSibling?.textContent?.trim();
        return labelText === '0';
      }

      #setupPaymentButtonValidation() {
        this.#boundOnPaymentButtonClick = this.#onPaymentButtonClick.bind(this);
        document.addEventListener('click', this.#boundOnPaymentButtonClick, true);
      }

      #onPaymentButtonClick(event) {
        const paymentButton = event.target.closest('.shopify-payment-button');
        if (!paymentButton) return;
        const clickedProductForm = paymentButton.closest('product-form');
        const ourProductForm = this.parentForm?.closest('product-form') || this.closest('product-form');
        if (clickedProductForm !== ourProductForm) return;
        if (!window.builderValidation.validateForForm(ourProductForm)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }

      #onFormSubmit(event) {
        if (!this.#validate()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.#showErrors();
        }
      }

      #getVisibleSlotCount() {
        if (this.dropdownButton) {
          const checked = this.querySelector('[data-dropdown-input]:checked');
          return checked ? parseInt(checked.dataset.slotCount, 10) : 1;
        }
        return this.slots.length;
      }

      // ========== Storage ==========

      #getStorageKey() {
        return `grouped-input-${this.productId}-${this.sectionId}`;
      }

      #getDropdownStorageKey() {
        return `grouped-input-dropdown-${this.productId}-${this.sectionId}`;
      }

      #getStoredData() {
        try {
          return JSON.parse(sessionStorage.getItem(this.#getStorageKey()) || '{}');
        } catch {
          return {};
        }
      }

      #migrateStorageFormat() {
        const data = this.#getStoredData();
        if (data.fields || (!data.selects && !data.texts)) return;
        const fields = {};
        if (data.selects) {
          for (const [slotIdx, value] of Object.entries(data.selects)) {
            if (!fields[slotIdx]) fields[slotIdx] = {};
            const selectFieldIndex = this.fieldConfig.findIndex((f) => f.type === 'select');
            fields[slotIdx][selectFieldIndex >= 0 ? selectFieldIndex : 0] = value;
          }
        }
        if (data.texts) {
          for (const [slotIdx, value] of Object.entries(data.texts)) {
            if (!fields[slotIdx]) fields[slotIdx] = {};
            const textFieldIndex = this.fieldConfig.findIndex((f) => f.type === 'text');
            fields[slotIdx][textFieldIndex >= 0 ? textFieldIndex : 1] = value;
          }
        }
        sessionStorage.setItem(this.#getStorageKey(), JSON.stringify({ fields }));
      }

      #saveFieldValue(slotIndex, fieldIndex, value) {
        const data = this.#getStoredData();
        if (!data.fields) data.fields = {};
        if (!data.fields[slotIndex]) data.fields[slotIndex] = {};

        if (value) {
          data.fields[slotIndex][fieldIndex] = value;
        } else {
          delete data.fields[slotIndex][fieldIndex];
        }

        sessionStorage.setItem(this.#getStorageKey(), JSON.stringify(data));
      }

      #getFieldValue(slotIndex, fieldIndex) {
        const data = this.#getStoredData();
        return data.fields?.[slotIndex]?.[fieldIndex] || '';
      }

      #clearStoredDataAfter(slotIndex) {
        const data = this.#getStoredData();
        if (!data.fields) return;
        let changed = false;
        for (const k of Object.keys(data.fields)) {
          if (parseInt(k, 10) > slotIndex) {
            delete data.fields[k];
            changed = true;
          }
        }
        if (changed) sessionStorage.setItem(this.#getStorageKey(), JSON.stringify(data));
      }

      // ========== Select Field Handlers ==========

      #onSelectChange(event) {
        const input = event.target;
        const slotIndex = parseInt(input.dataset.slotIndex, 10);
        const fieldIndex = parseInt(input.dataset.fieldIndex, 10);
        const value = input.value;

        this.#updateButtonText(slotIndex, fieldIndex, value, 'select');
        this.#saveFieldValue(slotIndex, fieldIndex, value);

        if (!value) {
          this.#clearSlotFieldsAfter(slotIndex);
        }

        this.#updateSlotStates();
        const highestFilled = this.#getHighestFilledSlotNumber();
        this.#updateNumberVariant(highestFilled);
        if (value) this.#clearFieldError(slotIndex, fieldIndex);
      }

      // ========== Text Field Handlers ==========

      #onTextInput(event) {
        const input = event.target;
        const slotIndex = parseInt(input.dataset.slotIndex, 10);
        const fieldIndex = parseInt(input.dataset.fieldIndex, 10);
        const fieldDef = this.fieldConfig[fieldIndex];

        if (fieldDef) {
          const pattern = this.constructor.VALIDATION_PATTERNS[fieldDef.validation];
          if (pattern) input.value = input.value.replace(pattern, '');

          const maxLen = fieldDef.maxLength || 0;
          if (maxLen > 0 && input.value.length > maxLen) {
            input.value = input.value.substring(0, maxLen);
          }

          this.#updateCharCounter(input, fieldIndex, input.value.length);
        }

        this.#saveFieldValue(slotIndex, fieldIndex, input.value);
        if (input.value.trim()) this.#clearFieldError(slotIndex, fieldIndex);
      }

      // ========== Charm Field Handlers ==========

      #onCharmOptionClick(event) {
        const optionBtn = event.target.closest('[data-charm-option]');
        if (!optionBtn) return;
        const slotIndex = parseInt(optionBtn.dataset.slotIndex, 10);
        const fieldIndex = parseInt(optionBtn.dataset.fieldIndex, 10);
        const value = optionBtn.dataset.value || '';

        const hiddenInput = this.querySelector(
          `[data-charm-input][data-slot-index="${slotIndex}"][data-field-index="${fieldIndex}"]`,
        );
        if (hiddenInput) hiddenInput.value = value;

        const imageInput = this.querySelector(
          `[data-charm-image-input][data-slot-index="${slotIndex}"][data-field-index="${fieldIndex}"]`,
        );
        if (imageInput) imageInput.value = optionBtn.dataset.image || '';

        this.#updateButtonText(slotIndex, fieldIndex, value, 'charm');
        this.#saveFieldValue(slotIndex, fieldIndex, value);

        const overlay = optionBtn.closest('x-popover, x-drawer');
        if (overlay && typeof overlay.hide === 'function') overlay.hide();

        if (value) this.#clearFieldError(slotIndex, fieldIndex);
      }

      #onCharmRemoveClick(event) {
        event.stopPropagation();
        event.preventDefault();

        const removeBtn = event.target.closest('[data-charm-remove]');
        if (!removeBtn) return;

        const slotIndex = parseInt(removeBtn.dataset.slotIndex, 10);
        const fieldIndex = parseInt(removeBtn.dataset.fieldIndex, 10);

        const hiddenInput = this.querySelector(
          `[data-charm-input][data-slot-index="${slotIndex}"][data-field-index="${fieldIndex}"]`,
        );
        if (hiddenInput) hiddenInput.value = '';

        const imageInput = this.querySelector(
          `[data-charm-image-input][data-slot-index="${slotIndex}"][data-field-index="${fieldIndex}"]`,
        );
        if (imageInput) imageInput.value = '';

        this.#updateButtonText(slotIndex, fieldIndex, '', 'charm');
        this.#saveFieldValue(slotIndex, fieldIndex, '');
        this.#updateSlotStates();

        this.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // ========== Generic Button Text ==========

      #updateButtonText(slotIndex, fieldIndex, value, fieldType) {
        let button;
        if (fieldType === 'select') {
          button = this.querySelector(
            `[data-select-button][data-slot-index="${slotIndex}"][data-field-index="${fieldIndex}"]`,
          );
        } else if (fieldType === 'charm') {
          button = this.querySelector(
            `[data-charm-button][data-slot-index="${slotIndex}"][data-field-index="${fieldIndex}"]`,
          );
        }
        if (!button) return;

        const textSpan = button.querySelector('[data-button-text]');
        if (!textSpan) return;

        const fieldDef = this.fieldConfig[fieldIndex];

        if (!value) {
          textSpan.textContent = fieldDef?.placeholder || '';
          if (fieldType === 'charm') {
            button.classList.remove('charm-selector__pill--has-value');
            const removeBtn = button.querySelector('[data-charm-remove]');
            if (removeBtn) removeBtn.hidden = true;
          }
          return;
        }

        const slotPrice = button.dataset.slotPrice;
        textSpan.textContent = slotPrice ? `${value} + ${slotPrice.replace(/<[^>]*>/g, '').trim()}` : value;

        if (fieldType === 'charm') {
          button.classList.add('charm-selector__pill--has-value');
          const removeBtn = button.querySelector('[data-charm-remove]');
          if (removeBtn) removeBtn.hidden = false;
        }
      }

      #updateCharCounter(input, fieldIndex, length) {
        const maxLen = this.fieldConfig[fieldIndex]?.maxLength || 0;
        if (maxLen <= 0) return;
        const counter = input.closest('.grouped-input-overlay__text-wrapper')?.querySelector('[data-char-counter]');
        if (counter) counter.textContent = `${length}/${maxLen}`;
      }

      // ========== Slot State Management ==========

      #isSlotFilled(slotIndex) {
        for (const fieldDef of this.fieldConfig) {
          if (!fieldDef.required) continue;
          const value = this.#getFieldValue(slotIndex, fieldDef.index);
          if (!value) return false;
        }
        return true;
      }

      #updateSlotStates() {
        if (this.dropdownButton) return;

        let previousFilled = true;
        for (const slot of this.slots) {
          const index = parseInt(slot.dataset.slotIndex, 10);
          const isEnabled = index === 0 || previousFilled;

          for (const field of slot.querySelectorAll('[data-field]')) {
            const btn = field.querySelector('[data-select-button], [data-charm-button]');
            const input = field.querySelector('[data-text-input]');
            if (btn) btn.disabled = !isEnabled;
            if (input) input.disabled = !isEnabled;

            for (const radio of field.querySelectorAll('[data-select-input]')) {
              radio.disabled = !isEnabled;
            }
          }

          if (index === 0) {
            previousFilled = this.#isSlotFilled(0);
          } else {
            previousFilled = previousFilled && this.#isSlotFilled(index);
          }
        }
      }

      #getHighestFilledSlotNumber() {
        let highest = 0;
        for (let i = 0; i < this.slots.length; i++) {
          if (this.#isSlotFilled(i)) {
            highest = i + 1;
          } else {
            break;
          }
        }
        return highest;
      }

      #clearSlotFieldsAfter(slotIndex) {
        for (const slot of this.slots) {
          const idx = parseInt(slot.dataset.slotIndex, 10);
          if (idx <= slotIndex) continue;

          for (const field of slot.querySelectorAll('[data-field]')) {
            const ft = field.dataset.fieldType;
            const fi = parseInt(field.dataset.fieldIndex, 10);

            if (ft === 'select') {
              for (const radio of field.querySelectorAll('[data-select-input]')) radio.checked = false;
              this.#updateButtonText(idx, fi, '', 'select');
            } else if (ft === 'text') {
              const input = field.querySelector('[data-text-input]');
              if (input) {
                input.value = '';
                this.#updateCharCounter(input, fi, 0);
              }
            } else if (ft === 'charm') {
              const hidden = field.querySelector('[data-charm-input]');
              if (hidden) hidden.value = '';
              this.#updateButtonText(idx, fi, '', 'charm');
            }

            this.#saveFieldValue(idx, fi, '');
          }
        }
      }

      #updateNumberVariant(targetNumber) {
        const targetValue = targetNumber > 0 ? String(targetNumber) : '1';
        for (const radio of this.variantRadios) {
          const label = radio.nextElementSibling;
          const labelText = label?.textContent?.trim();
          if (labelText === targetValue) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }

      // ========== Restore Selections ==========

      #restoreSelections() {
        const data = this.#getStoredData();
        const fields = data.fields || {};

        for (const [slotIdx, slotFields] of Object.entries(fields)) {
          const si = parseInt(slotIdx, 10);
          for (const [fieldIdx, value] of Object.entries(slotFields)) {
            const fi = parseInt(fieldIdx, 10);
            if (!value) continue;

            const fieldDef = this.fieldConfig[fi];
            if (!fieldDef) continue;

            if (fieldDef.type === 'select') {
              this.#updateButtonText(si, fi, value, 'select');
              const radio = this.querySelector(
                `[data-select-input][data-slot-index="${si}"][data-field-index="${fi}"][value="${CSS.escape(value)}"]`,
              );
              if (radio) radio.checked = true;
            } else if (fieldDef.type === 'text') {
              const input = this.querySelector(`[data-text-input][data-slot-index="${si}"][data-field-index="${fi}"]`);
              if (input) {
                input.value = value;
                this.#updateCharCounter(input, fi, value.length);
              }
            } else if (fieldDef.type === 'charm') {
              this.#updateButtonText(si, fi, value, 'charm');
              const hidden = this.querySelector(
                `[data-charm-input][data-slot-index="${si}"][data-field-index="${fi}"]`,
              );
              if (hidden) hidden.value = value;
              const charmBtn = this.querySelector(
                `[data-charm-option][data-slot-index="${si}"][data-field-index="${fi}"][data-value="${CSS.escape(value)}"]`,
              );
              const imgHidden = this.querySelector(
                `[data-charm-image-input][data-slot-index="${si}"][data-field-index="${fi}"]`,
              );
              if (imgHidden && charmBtn) imgHidden.value = charmBtn.dataset.image || '';
            }
          }
        }

        this.#updateSlotStates();
      }

      // ========== Validation ==========

      #validate() {
        if (this.dataset.optional === 'true' && this.#isZeroSelectedForNumberOption()) return true;

        const visibleCount = this.#getVisibleSlotCount();

        if (this.dropdownButton) {
          for (let i = 0; i < visibleCount; i++) {
            if (!this.#validateSlot(i)) return false;
          }
          return true;
        }

        for (let i = 0; i < this.slots.length; i++) {
          if (!this.#isSlotFilled(i)) {
            // Slot 0 is required; subsequent unfilled slots are OK (progressive fill)
            return i > 0;
          }
          if (!this.#validateSlot(i)) return false;
        }
        return true;
      }

      #validateSlot(slotIndex) {
        for (const fieldDef of this.fieldConfig) {
          if (!fieldDef.required) continue;
          if (!this.#validateField(slotIndex, fieldDef)) return false;
        }
        return true;
      }

      #validateField(slotIndex, fieldDef) {
        const slot = this.slots[slotIndex];
        if (!slot || slot.hidden) return true;

        const value = this.#getFieldValue(slotIndex, fieldDef.index);

        if (fieldDef.type === 'select' || fieldDef.type === 'charm') {
          return value !== '';
        }

        if (fieldDef.type === 'text') {
          if (!value.trim()) return false;
          const pattern = this.constructor.VALIDATION_PATTERNS[fieldDef.validation];
          if (pattern && value !== value.replace(pattern, '')) return false;
          const maxLen = fieldDef.maxLength || 0;
          if (maxLen > 0 && value.length > maxLen) return false;
          const input = slot.querySelector(`[data-text-input][data-field-index="${fieldDef.index}"]`);
          if (input && input.pattern && !new RegExp(`^${input.pattern}$`).test(value)) return false;
          return true;
        }

        return true;
      }

      #showErrors() {
        let firstErrorShown = false;
        const visibleCount = this.#getVisibleSlotCount();

        for (let i = 0; i < visibleCount; i++) {
          const slot = this.slots[i];
          if (!slot || slot.hidden) continue;

          for (const fieldDef of this.fieldConfig) {
            if (!fieldDef.required) continue;
            if (this.#validateField(i, fieldDef)) continue;

            const field = slot.querySelector(`[data-field][data-field-index="${fieldDef.index}"]`);
            if (!field) continue;

            const label = field.querySelector('.grouped-input-overlay__label');
            const duration = this.constructor.ERROR_DISPLAY_MS;
            if (label) {
              label.style.color = 'rgb(var(--error-text, 220 38 38))';
              setTimeout(() => {
                label.style.color = '';
              }, duration);
            }

            const errorEl = field.querySelector('[data-field-error]');

            if (fieldDef.type === 'select') {
              const btn = field.querySelector('[data-select-button]');
              if (btn) {
                btn.classList.add('grouped-input-overlay__button--error');
                setTimeout(() => btn.classList.remove('grouped-input-overlay__button--error'), duration);
              }
              if (errorEl) {
                errorEl.textContent = this.errorMessage;
                errorEl.hidden = false;
              }
            } else if (fieldDef.type === 'text') {
              const input = field.querySelector('[data-text-input]');
              if (input) {
                input.classList.add('grouped-input-overlay__input--error');
                setTimeout(() => input.classList.remove('grouped-input-overlay__input--error'), duration);
              }
              const msg =
                input?.pattern && !new RegExp(`^${input.pattern}$`).test(input?.value || '')
                  ? this.dataset.textPatternMessage || 'Invalid input'
                  : this.requiredMessage;
              if (errorEl) {
                errorEl.textContent = msg;
                errorEl.hidden = false;
              }
            } else if (fieldDef.type === 'charm') {
              const btn = field.querySelector('[data-charm-button]');
              if (btn) {
                btn.classList.add('charm-selector__pill--error');
                setTimeout(() => btn.classList.remove('charm-selector__pill--error'), duration);
              }
            }

            if (!firstErrorShown) {
              document.dispatchEvent(
                new CustomEvent('toast:show', {
                  detail: { message: this.errorMessage, tone: 'error' },
                }),
              );
              firstErrorShown = true;
            }
          }
        }
      }

      #clearFieldError(slotIndex, fieldIndex) {
        const slot = this.slots[slotIndex];
        if (!slot) return;
        const field = slot.querySelector(`[data-field][data-field-index="${fieldIndex}"]`);
        if (!field) return;

        const label = field.querySelector('.grouped-input-overlay__label');
        if (label) label.style.color = '';

        const btn = field.querySelector('[data-select-button], [data-charm-button]');
        if (btn) {
          btn.classList.remove('grouped-input-overlay__button--error');
          btn.classList.remove('charm-selector__pill--error');
        }

        const input = field.querySelector('[data-text-input]');
        if (input) input.classList.remove('grouped-input-overlay__input--error');

        const errorEl = field.querySelector('[data-field-error]');
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
      }

      // ========== Dropdown Methods ==========

      #initializeDropdown() {
        let config = { defaultIndex: 0, defaultSlotCount: 1 };
        try {
          config = JSON.parse(this.dropdownConfigEl?.textContent || '{}');
        } catch {
          /* ignore */
        }

        const storageKey = this.#getDropdownStorageKey();
        const storedData = sessionStorage.getItem(storageKey);
        let storedSlotCount = null;

        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            if (parsed.pageLoadId === getGroupedOverlayPageLoadId()) {
              storedSlotCount = parsed.slotCount;
            }
          } catch {
            /* ignore */
          }
        }

        const visibleInputs = Array.from(this.dropdownInputs);
        const alreadyCheckedInput = visibleInputs.find((input) => input.checked);

        let targetSlotCount;
        if (storedSlotCount) {
          targetSlotCount = parseInt(storedSlotCount, 10);
        } else if (alreadyCheckedInput) {
          targetSlotCount = parseInt(alreadyCheckedInput.dataset.slotCount, 10);
        } else {
          targetSlotCount = config.defaultSlotCount;
        }

        const targetInput =
          visibleInputs.find((input) => parseInt(input.dataset.slotCount, 10) === targetSlotCount) || visibleInputs[0];

        const url = new URL(window.location.href);
        const hasVariantInUrl = url.searchParams.has('variant');

        if (targetInput) {
          if (!targetInput.checked) targetInput.checked = true;
          if (!storedSlotCount && !hasVariantInUrl) {
            targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        const checkedInput = this.querySelector('[data-dropdown-input]:checked');
        if (checkedInput) {
          const buttonText = this.querySelector('[data-dropdown-text]');
          if (buttonText) buttonText.innerHTML = checkedInput.dataset.displayLabel;
          this.#updateVisibleSlots(parseInt(checkedInput.dataset.slotCount, 10));
        }

        this.#restoreSelections();
      }

      #onDropdownChange(event) {
        const input = event.target;
        const targetCount = parseInt(input.dataset.slotCount, 10);

        sessionStorage.setItem(
          this.#getDropdownStorageKey(),
          JSON.stringify({ slotCount: targetCount.toString(), pageLoadId: getGroupedOverlayPageLoadId() }),
        );

        const buttonText = this.querySelector('[data-dropdown-text]');
        if (buttonText) buttonText.innerHTML = input.dataset.displayLabel;

        this.#updateVisibleSlots(targetCount);
        this.#clearStoredDataAfter(targetCount - 1);
        this.#clearSlotFieldsAfter(targetCount - 1);
      }

      #updateVisibleSlots(count) {
        for (let i = 0; i < this.slots.length; i++) {
          const slot = this.slots[i];
          const isVisible = i < count;
          slot.hidden = !isVisible;

          for (const field of slot.querySelectorAll('[data-field]')) {
            const btn = field.querySelector('[data-select-button], [data-charm-button]');
            const input = field.querySelector('[data-text-input]');
            if (btn) btn.disabled = !isVisible;
            if (input) input.disabled = !isVisible;

            for (const radio of field.querySelectorAll('[data-select-input]')) {
              radio.disabled = !isVisible;
            }
          }
        }
      }
    },
  );
}

/**
 * Sticky Add-to-Cart Scroll Handler
 * Handles scrolling to the product form when "Select Options" is clicked
 */
document.addEventListener('click', (event) => {
  // Check if click is on a button inside [data-scroll-to-form] or on the wrapper itself
  const scrollWrapper = event.target.closest('[data-scroll-to-form]');
  if (!scrollWrapper) return;

  // Find the sticky add-to-cart parent and get the form ID
  const stickyATC = scrollWrapper.closest('product-sticky-add-to-cart');
  if (!stickyATC) return;

  // Prevent any default button behavior
  event.preventDefault();

  const formId = stickyATC.getAttribute('main-product-form');
  const form = formId ? document.getElementById(formId) : null;

  if (form) {
    // Find the variant picker or overlay components to scroll to
    const sectionScope = form.closest('[id^="shopify-section-"]');
    const scrollTarget =
      sectionScope?.querySelector('.variant-picker-wrapper') ||
      sectionScope?.querySelector('variant-picker') ||
      sectionScope?.querySelector(
        'select-input-overlay, charm-selector-overlay, text-input-overlay, grouped-input-overlay, builder-text-input, builder-select, builder-charm-selector',
      ) ||
      form;

    // Scroll to the target with some offset for better visibility
    const offset = 120;
    const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    });
  }
});

/**
 * Sticky ATC Dynamic Text Manager
 * Switches between "Select Options" and "Add to Cart" based on form validity.
 * Handles click-to-submit when form is valid.
 */
const stickyATCManager = {
  _isValid: false,

  // Check if all overlay/builder components have values (without showing error UI)
  checkFormValidity(stickyATC) {
    const formId = stickyATC.getAttribute('main-product-form');
    const form = formId ? document.getElementById(formId) : null;
    if (!form) return false;

    const sectionScope = form.closest('[id^="shopify-section-"]') || form;

    // Match all component types that actually exist
    const selector = [
      'select-input-overlay',
      'charm-selector-overlay',
      'text-input-overlay',
      'grouped-input-overlay',
      'builder-text-input',
      'builder-select',
      'builder-upload',
      'builder-charm-selector',
    ].join(',');

    const components = sectionScope.querySelectorAll(selector);
    for (const component of components) {
      if (!this.componentHasValue(component)) return false;
    }

    return true;
  },

  componentHasValue(component) {
    const tagName = component.tagName.toLowerCase();

    if (tagName === 'select-input-overlay') {
      // Check sessionStorage for stored values
      const storageKey = component.dataset.storageKey;
      if (storageKey) {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          try {
            const data = JSON.parse(stored);
            return data['0'] && data['0'].trim() !== '';
          } catch {
            return false;
          }
        }
      }
      // Fallback: check if button shows a selected value (not placeholder)
      const btn = component.querySelector('[data-select-button][data-select-index="0"]');
      if (btn) {
        const text = btn.textContent.trim();
        return text !== '' && !text.toLowerCase().includes('select');
      }
      return false;
    }

    if (tagName === 'charm-selector-overlay') {
      // Use the component's own isValid method if available (handles all modes correctly)
      if (typeof component.isValid === 'function') {
        return component.isValid();
      }
      // Fallback: Check if the pill has a value
      const pill = component.querySelector('[data-charm-pill][data-pill-index="0"]');
      if (pill && pill.classList.contains('charm-selector__pill--has-value')) {
        return true;
      }
      // Check sessionStorage as fallback
      const storageKey = component.dataset.storageKey;
      if (storageKey) {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          try {
            const data = JSON.parse(stored);
            return data['0'] && data['0'].trim() !== '';
          } catch {
            return false;
          }
        }
      }
      return false;
    }

    if (tagName === 'grouped-input-overlay') {
      const firstSlot = component.querySelector('[data-slot-index="0"]');
      if (!firstSlot) return false;
      for (const field of firstSlot.querySelectorAll('[data-field]')) {
        const ft = field.dataset.fieldType;
        if (ft === 'select' || ft === 'charm') {
          const btn = field.querySelector('[data-select-button], [data-charm-button]');
          const text = btn?.querySelector('[data-button-text]')?.textContent?.trim();
          if (!text || text === '') return false;
          let fieldConfig = [];
          try {
            fieldConfig = JSON.parse(component.querySelector('[data-field-config]')?.textContent || '[]');
          } catch {
            /* */
          }
          const fi = parseInt(field.dataset.fieldIndex, 10);
          const placeholder = fieldConfig[fi]?.placeholder || '';
          if (text === placeholder) return false;
        } else if (ft === 'text') {
          const input = field.querySelector('[data-text-input]');
          if (!input || !input.value.trim()) return false;
        }
      }
      return true;
    }

    if (tagName === 'text-input-overlay') {
      const input = component.querySelector('[data-text-input]');
      if (input) {
        return input.value.trim() !== '';
      }
      return false;
    }

    if (tagName === 'builder-text-input') {
      // Check if the input has a value and meets minlength requirement
      const input = component.querySelector('input[type="text"]');
      if (input) {
        const value = input.value.trim();
        const minLength = parseInt(input.getAttribute('minlength') || '0', 10);
        const isRequired = input.hasAttribute('required');

        if (isRequired && value === '') return false;
        if (minLength > 0 && value.length < minLength) return false;

        return true;
      }
      return false;
    }

    if (tagName === 'builder-select') {
      // Check if a non-placeholder option is selected
      const select = component.querySelector('select');
      if (select) {
        const isRequired = select.hasAttribute('required');
        const value = select.value;

        if (isRequired && (!value || value === '')) return false;

        return true;
      }
      return false;
    }

    if (tagName === 'builder-upload') {
      // Check if a file has been uploaded
      const input = component.querySelector('input[type="file"]');
      const isRequired = input?.hasAttribute('required') || component.hasAttribute('required');

      if (isRequired) {
        // Check for uploaded file indicator or hidden input with value
        const hasFile =
          component.querySelector('[data-uploaded-file]') || component.querySelector('input[type="hidden"][value]');
        return !!hasFile;
      }
      return true;
    }

    if (tagName === 'builder-charm-selector') {
      // Use the component's own isValid method if available
      if (typeof component.isValid === 'function') {
        return component.isValid();
      }
      // Fallback: check if property input has value
      const propertyInput = component.querySelector('[data-property-input]');
      const isRequired = component.dataset.required === 'true';
      if (isRequired) {
        return propertyInput?.value?.trim() !== '';
      }
      return true;
    }

    return true;
  },

  updateStickyATC(stickyATC, isValid) {
    this._isValid = isValid;
    stickyATC.dataset.formValid = isValid ? 'true' : 'false';

    const wrappers = stickyATC.querySelectorAll('[data-scroll-to-form], [data-sticky-atc-wrapper]');

    for (const wrapper of wrappers) {
      const state = isValid ? 'valid' : 'invalid';
      wrapper.setAttribute('data-sticky-atc-wrapper', state);
    }
  },

  init() {
    const checkAndUpdate = () => {
      const stickyATC = document.querySelector('product-sticky-add-to-cart[data-has-overlay-options]');
      if (!stickyATC) return;

      const isValid = this.checkFormValidity(stickyATC);
      this.updateStickyATC(stickyATC, isValid);
    };

    // Handle clicks on sticky ATC buttons - submit form if valid
    document.addEventListener(
      'click',
      (event) => {
        const stickyATC = event.target.closest('product-sticky-add-to-cart[data-has-overlay-options]');
        if (!stickyATC) return;

        const button = event.target.closest('button');
        if (!button) return;

        const wrapper = button.closest('[data-sticky-atc-wrapper], [data-scroll-to-form]');
        if (!wrapper) return;

        // Re-check validity at click time to handle any race conditions
        const isValid = this.checkFormValidity(stickyATC);

        if (isValid) {
          // Submit the form directly
          event.preventDefault();
          event.stopPropagation();

          // Show loading state on the sticky button
          button.setAttribute('aria-busy', 'true');

          // Also show the loading bar for visual feedback
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

          const formId = stickyATC.getAttribute('main-product-form');
          const form = formId ? document.getElementById(formId) : null;

          if (form) {
            // Find the product-form element and its submit button
            const productForm = form.closest('product-form') || form;
            const submitBtn = productForm.querySelector('button[type="submit"]');

            if (submitBtn) {
              submitBtn.click();
            } else {
              // Fallback: dispatch submit event
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            }
          }
        }
        // If not valid, let the scroll-to-form handler take over
      },
      true,
    ); // Use capture phase to run before scroll handler

    // Remove loading state when cart operation completes
    const clearLoadingState = () => {
      const buttons = document.querySelectorAll('product-sticky-add-to-cart button[aria-busy="true"]');
      buttons.forEach((btn) => btn.removeAttribute('aria-busy'));
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    };

    document.addEventListener('cart:change', clearLoadingState);
    document.addEventListener('cart:error', clearLoadingState);
    document.addEventListener('variant:add', clearLoadingState);

    // Listen for input/change events on all builder/overlay components
    const componentSelector = [
      'select-input-overlay',
      'charm-selector-overlay',
      'text-input-overlay',
      'builder-text-input',
      'builder-select',
      'builder-upload',
      'builder-charm-selector',
    ].join(',');

    document.addEventListener('input', (event) => {
      const component = event.target.closest(componentSelector);
      if (!component) return;

      clearTimeout(this._checkTimeout);
      this._checkTimeout = setTimeout(checkAndUpdate, 50);
    });

    document.addEventListener('change', (event) => {
      const component = event.target.closest(componentSelector);
      if (!component) return;

      clearTimeout(this._checkTimeout);
      this._checkTimeout = setTimeout(checkAndUpdate, 50);
    });

    // Listen for click events on overlay buttons (for select/charm overlays)
    document.addEventListener('click', (event) => {
      const overlayButton = event.target.closest(
        'select-input-overlay button, charm-selector-overlay button, builder-charm-selector button',
      );
      if (!overlayButton) return;

      // Delay check to allow overlay to update
      setTimeout(checkAndUpdate, 150);
    });

    // Check on drawer/dialog close (charm/select selections happen in drawers)
    document.addEventListener('dialog:after-hide', () => {
      // Small delay to ensure values are stored
      setTimeout(checkAndUpdate, 50);
    });

    // Initial check on page load
    setTimeout(checkAndUpdate, 300);
  },
};

/**
 * Variant Dropdown
 * Optimistic button-text update for variant picker dropdowns.
 * Copies the selected label content into the trigger button immediately on change,
 * so the buyer sees feedback before the server re-render arrives.
 */
if (!customElements.get('variant-dropdown')) {
  customElements.define(
    'variant-dropdown',
    class extends HTMLElement {
      #boundOnChange;

      connectedCallback() {
        this.#boundOnChange = this.#onChange.bind(this);
        this.addEventListener('change', this.#boundOnChange);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.#boundOnChange);
      }

      #onChange(event) {
        const input = event.target;
        if (input.type !== 'radio' || !input.hasAttribute('data-option-position')) return;

        const popover = input.closest('x-popover');
        if (!popover) return;

        const selectedValueSpan = document.getElementById(popover.id + '-selected-value');
        if (!selectedValueSpan) return;

        const label = input.closest('label');
        if (!label) return;

        const sizeInfo = label.querySelector('.size-option__info');
        selectedValueSpan.innerHTML = sizeInfo
          ? sizeInfo.outerHTML
          : label.querySelector('span')?.outerHTML || '';
      }
    },
  );
}

// Initialize sticky ATC manager
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => stickyATCManager.init());
} else {
  stickyATCManager.init();
}
